import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import vacantesService from '../../../api/vacantesService';
import {
  Plus, Users, Briefcase, CheckCircle, XCircle, Clock, Calendar,
  DollarSign, MapPin, Eye, FileText, UserCheck, Send, Edit, Loader,
  AlertCircle, X, ThumbsUp, ThumbsDown, ArrowRight, Building2,
  Star, MessageSquare, ChevronDown, ChevronUp, Filter, Search,
  LayoutGrid, List, Award, TrendingUp, Phone, Mail, User,
  Layers, BookOpen, Target, ClipboardList, History, ChevronRight,
  Download, ExternalLink, Globe, GraduationCap,
  Zap, BarChart2, PieChart, Activity, RefreshCw,
  FileCheck, FileBadge, Image, Paperclip, Hash, Circle,
  UserCircle, ArrowUpRight, Megaphone, AlertTriangle, UserRound
} from 'lucide-react';
import SearchableSelect from '../../shared/SearchableSelect';

// ============================================================
// PIPELINE CONFIG
// ============================================================
const PIPELINE = [
  { id: 'Enviado',                  label: 'Nuevo',       color: '#3b82f6', light: '#eff6ff',  icon: Send,        desc: 'Postulación recibida' },
  { id: 'Recibido en DTHS',        label: 'Recibido',    color: '#0891b2', light: '#ecfeff',  icon: FileCheck,   desc: 'Confirmado por DTHS' },
  { id: 'Visto en DTHS',           label: 'Revisando',   color: '#7c3aed', light: '#f5f3ff',  icon: Eye,         desc: 'Revisión inicial' },
  { id: 'En Proceso de Revisión',  label: 'Evaluación',  color: '#d97706', light: '#fffbeb',  icon: Activity,    desc: 'Evaluación profunda' },
  { id: 'Postulación Aprobada',    label: 'Aprobado',    color: '#16a34a', light: '#f0fdf4',  icon: CheckCircle, desc: 'Candidato seleccionado' },
  { id: 'Aprobación Declinada',    label: 'Declinado',   color: '#dc2626', light: '#fef2f2',  icon: XCircle,     desc: 'No seleccionado' },
];

const PROXY_NEXT = {
  'Enviado': 'Recibido en DTHS',
  'Recibido en DTHS': 'Visto en DTHS',
  'Visto en DTHS': 'En Proceso de Revisión',
};

// ── Normaliza variantes del estado al ID canónico del pipeline ──
// CORRECTO — añadir al mapa:
const normalizarEstado = (estado) => {
  const mapa = {
    // Aliases cortos → estado canónico del pipeline RRHH
    'Recibida':   'Recibido en DTHS',
    'Recibido':   'Recibido en DTHS',
    'Nuevo':      'Enviado',
    'Nueva':      'Enviado',
    'Enviada':    'Enviado',
    'Revisando':  'Visto en DTHS',
    'Visto':      'Visto en DTHS',
    'En Revisión':'En Proceso de Revisión',
    'En Proceso': 'En Proceso de Revisión',
    'Evaluación': 'En Proceso de Revisión',
    'Aprobada':   'Postulación Aprobada',
    'Aprobado':   'Postulación Aprobada',
    'Declinada':  'Aprobación Declinada',
    'Declinado':  'Aprobación Declinada',
    'Rechazada':  'Aprobación Declinada',

    // ✅ Estados jerárquicos — cada uno se mapea a sí mismo
    'Pendiente Validación Supervisor': 'Pendiente Validación Supervisor',
    'Pendiente Aprobación Gerente':    'Pendiente Aprobación Gerente',
    'Pendiente Aprobación Director':   'Pendiente Aprobación Director',
    'Rechazada por Supervisor':        'Rechazada por Supervisor',
  };
  return mapa[estado] || estado;
};

const getEstadoConfig = (estado) =>
  PIPELINE.find(p => p.id === normalizarEstado(estado)) || { color: '#64748b', light: '#f8fafc', label: estado, icon: Circle };

// ============================================================
// MAIN MODULE
// ============================================================
const VacantesModule = () => {
  const { user, loading, getStoredToken } = useAuth();
  const [vistaActiva, setVistaActiva] = useState('vacantes');
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [solicitudes, setSolicitudes] = useState([]);
  const [vacantes, setVacantes] = useState([]);
  const [postulaciones, setPostulaciones] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});

  const [modalSolicitud, setModalSolicitud] = useState(false);
  const [modalVacante, setModalVacante] = useState(false);
  const [modalPostulacion, setModalPostulacion] = useState(false);
  const [vacanteSeleccionada, setVacanteSeleccionada] = useState(null);
  const [modalEditarVacante, setModalEditarVacante] = useState(false);
  const [modalCerrarVacante, setModalCerrarVacante] = useState(false);
  const [modalFichaPostulante, setModalFichaPostulante] = useState(false);
  const [postulacionSeleccionada, setPostulacionSeleccionada] = useState(null);

  useEffect(() => {
    if (!loading && user) cargarDatos();
  }, [user, loading]);

  const cargarDatos = async () => {
    try {
      setLoadingData(true);
      const token = getStoredToken();
      const [deptData, statsData, vacantesData] = await Promise.all([
        vacantesService.getDepartamentos(token),
        vacantesService.getEstadisticas(user.role, token),
        vacantesService.getVacantesActivas(token)
      ]);
      setDepartamentos(deptData || []);
      setEstadisticas(statsData || {});
      setVacantes(vacantesData || []);

      const rolNorm = user.role?.toLowerCase().replace(/\s+/g, '_');
      try {
        if (['director','gerente','coordinador','dths_director','dths_gerente','dths_analista','dths_coordinador'].includes(rolNorm)) {
          const solData = await vacantesService.getSolicitudes(user.id, user.role);
          setSolicitudes(solData || []);
        }
        if (['dths_director','dths_gerente','dths_analista','dths_coordinador'].includes(rolNorm)) {
          const postData = await vacantesService.getPostulaciones('todas', token);
          setPostulaciones(postData || []);
        } else if (['gerente','director','coordinador'].includes(rolNorm)) {
          if (user.empleadoId) {
            const postData = await vacantesService.getPostulacionesEquipo(
              user.empleadoId,
              token,
              rolNorm  // ← el backend ahora sabe qué estados filtrar según el rol
            );
            setPostulaciones(postData || []);
          }
        } else if (rolNorm === 'colaborador' && user.empleadoId) {
          const postData = await vacantesService.getPostulacionesEmpleado(user.empleadoId, token);
          setPostulaciones(postData || []);
        }
      } catch (postErr) {
        // Las solicitudes/postulaciones son secundarias: si fallan no deben
        // impedir ver el listado de vacantes ni el botón de "Postularme".
        console.error('Error al cargar solicitudes/postulaciones:', postErr);
      }
    } catch (err) {
      setError('Error al cargar datos del sistema');
    } finally {
      setLoadingData(false);
    }
  };

  const rolNormalizado = user?.role?.toLowerCase().replace(/\s+/g, '_');
  const permisos = {
    crearSolicitud: ['director','gerente','coordinador'].includes(rolNormalizado),
    aprobarDirectorArea: ['director'].includes(rolNormalizado),
    aprobarGerenteRRHH: ['dths_gerente'].includes(rolNormalizado),
    aprobarDirectorRRHH: ['dths_director'].includes(rolNormalizado),
    crearVacante: ['dths_director','dths_gerente','dths_analista','dths_coordinador'].includes(rolNormalizado),
    publicarVacante: ['dths_director','dths_gerente','dths_analista','dths_coordinador'].includes(rolNormalizado),
    postularse: ['colaborador','coordinador'].includes(rolNormalizado),
    gestionarPostulaciones: ['dths_director','dths_gerente','dths_analista','dths_coordinador','director','gerente'].includes(rolNormalizado),
    validarPostulacionEquipo: ['gerente','director','coordinador'].includes(rolNormalizado),
    verDashboard: ['dths_director','dths_gerente','dths_analista','dths_coordinador'].includes(rolNormalizado),
    gestionarEstadosRRHH: ['dths_director','dths_gerente','dths_analista','dths_coordinador'].includes(rolNormalizado)
  };

  const crearSolicitud = async (datos) => {
    try {
      const token = getStoredToken();
      const nueva = await vacantesService.crearSolicitudVacante({ ...datos, solicitanteId: user.empleadoId, estado: 'Pendiente' }, token);
      setSolicitudes(prev => [...prev, nueva]);
      setModalSolicitud(false);
      mostrarExito('Solicitud enviada al Área de Talento Humano');
    } catch (err) { setError('Error al crear solicitud'); }
  };

  const aprobarSolicitud = async (solicitudId, nivel) => {
    try {
      const token = getStoredToken();
      let nuevoEstado, mensaje;
      if (nivel === 'director-area') { await vacantesService.aprobarSolicitudDirectorArea(solicitudId, user.empleadoId, token); nuevoEstado = 'Aprobada por Director de Área'; mensaje = 'Solicitud aprobada'; }
      else if (nivel === 'gerente-dths') { await vacantesService.aprobarSolicitudGerenteRRHH(solicitudId, user.empleadoId, token); nuevoEstado = 'Aprobada por Gerente DTHS'; mensaje = 'Solicitud aprobada por Gerente DTHS'; }
      else if (nivel === 'director-dths') { await vacantesService.aprobarSolicitudDirectorRRHH(solicitudId, user.empleadoId, token); nuevoEstado = 'Aprobada por Director DTHS'; mensaje = 'Solicitud autorizada'; }
      setSolicitudes(prev => prev.map(s => s.id === solicitudId ? { ...s, estado: nuevoEstado } : s));
      mostrarExito(mensaje);
    } catch (err) { setError('Error al aprobar solicitud'); }
  };

  const rechazarSolicitud = async (solicitudId, comentarios) => {
    try {
      const token = getStoredToken();
      await vacantesService.rechazarSolicitud(solicitudId, comentarios, token);
      setSolicitudes(prev => prev.map(s => s.id === solicitudId ? { ...s, estado: 'Rechazada' } : s));
      mostrarExito('Solicitud rechazada');
    } catch (err) { setError('Error al rechazar solicitud'); }
  };

  const publicarVacante = async (solicitudId) => {
    try {
      const token = getStoredToken();
      const nuevaVacante = await vacantesService.publicarVacanteDesdeSolicitud(solicitudId, user.empleadoId, token);
      setSolicitudes(prev => prev.map(s => s.id === solicitudId ? { ...s, estado: 'Publicada' } : s));
      setVacantes(prev => [...prev, nuevaVacante]);
      mostrarExito('Vacante publicada');
    } catch (err) { setError('Error al publicar vacante'); }
  };

  const crearVacanteDirecta = async (datos) => {
    try {
      const token = getStoredToken();
      const nueva = await vacantesService.crearVacanteDirecta({ ...datos, creadoPor: user.empleadoId, estado: 'Activa' }, token);
      setVacantes(prev => [...prev, nueva]);
      setModalVacante(false);
      mostrarExito('Vacante creada y publicada');
    } catch (err) { setError('Error al crear vacante'); }
  };

  const editarVacante = async (vacanteId, datos) => {
    try {
      const token = getStoredToken();
      await vacantesService.actualizarVacante(vacanteId, datos, token);
      const vacanteActualizada = await vacantesService.getVacantePorId(vacanteId, token);
      setVacantes(prev => prev.map(v => v.id === vacanteId ? vacanteActualizada : v));
      setModalEditarVacante(false);
      setVacanteSeleccionada(null);
      mostrarExito('Vacante actualizada');
    } catch (err) { setError('Error al actualizar vacante'); }
  };

  const togglePublicacionVacante = async (vacanteId, publicarEnPortal) => {
    try {
      const token = getStoredToken();
      await vacantesService.actualizarVacante(vacanteId, { PublicaEnPortal: publicarEnPortal ? 1 : 0 }, token);
      setVacantes(prev => prev.map(v => v.id === vacanteId ? { ...v, PublicaEnPortal: publicarEnPortal ? 1 : 0 } : v));
      mostrarExito(publicarEnPortal ? 'Vacante publicada en portal' : 'Vacante removida del portal');
    } catch (err) { setError('Error al cambiar publicación'); }
  };

  const cambiarEstadoSolicitud = async (solicitudId, nuevoEstado) => {
    try {
      const token = getStoredToken();
      await vacantesService.cambiarEstadoSolicitud(solicitudId, nuevoEstado, token);
      setSolicitudes(prev => prev.map(s => s.id === solicitudId ? { ...s, estado: nuevoEstado } : s));
      mostrarExito(`Estado actualizado: ${nuevoEstado}`);
    } catch (err) { setError('Error al cambiar estado de solicitud'); }
  };

  const cerrarVacante = async (vacanteId, motivoCierre) => {
    try {
      const token = getStoredToken();
      await vacantesService.cerrarVacante(vacanteId, motivoCierre, user.empleadoId, token);
      setVacantes(prev => prev.map(v => v.id === vacanteId ? { ...v, estado: 'Cerrada', motivoCierre } : v));
      setModalCerrarVacante(false);
      setVacanteSeleccionada(null);
      mostrarExito('Vacante cerrada');
    } catch (err) { setError('Error al cerrar vacante'); }
  };

 const postularse = async (datos) => {
  try {
    const token = getStoredToken();
    const nueva = await vacantesService.crearPostulacion({
      ...datos, vacanteId: vacanteSeleccionada.id, empleadoId: user.empleadoId,
    }, token);
    setPostulaciones(prev => [...prev, nueva]);
    setModalPostulacion(false);
    setVacanteSeleccionada(null);
    mostrarExito('Postulación enviada. Tu supervisor ha sido notificado.');
  } catch (err) {
    const msg = err?.response?.data?.error || err?.message || 'Error al enviar postulación';
    setError(msg);
  }
};

const validarPostulacionSupervisor = async (postulacionId, aprobado) => {
  try {
    const token = getStoredToken();
    await vacantesService.validarPostulacionSupervisor(
      postulacionId, aprobado, user.empleadoId, token, user.role
    );

    const rolNorm = user.role?.toLowerCase().replace(/\s+/g, '_');
    let nuevoEstado;

    if (!aprobado) {
      nuevoEstado = 'Rechazada por Supervisor';
    } else if (rolNorm === 'coordinador') {
      nuevoEstado = 'Pendiente Aprobación Gerente';
    } else if (rolNorm === 'gerente') {
      nuevoEstado = 'Pendiente Aprobación Director';
    } else {
      nuevoEstado = 'Enviado';
    }

    setPostulaciones(prev => prev.map(p =>
      p.id === postulacionId ? { ...p, estado: nuevoEstado } : p
    ));
    mostrarExito(aprobado ? 'Postulación validada y escalada' : 'Postulación rechazada');
  } catch (err) {
    setError('Error al validar postulación');
  }
};

 const cambiarEstadoPostulacion = async (postulacionId, nuevoEstado, notas = '', empleadoId = null, calificacion = null, evaluacionDetalle = null) => {
  try {
    const token = getStoredToken();
    await vacantesService.cambiarEstadoPostulacion(postulacionId, nuevoEstado, notas, empleadoId || user.empleadoId, token, calificacion, evaluacionDetalle);
      const ahora = new Date().toISOString();
      setPostulaciones(prev => prev.map(p => {
        if (p.id !== postulacionId) return p;
        const update = { ...p, estado: nuevoEstado };
        if (nuevoEstado === 'Recibido en DTHS') update.fechaRecibidoRRHH = ahora;
        if (nuevoEstado === 'Visto en DTHS') update.fechaVistoRRHH = ahora;
        if (nuevoEstado === 'En Proceso de Revisión') update.fechaRevision = ahora;
        return update;
      }));
      if (postulacionSeleccionada?.id === postulacionId) {
        setPostulacionSeleccionada(prev => {
          const update = { ...prev, estado: nuevoEstado };
          const ahora2 = new Date().toISOString();
          if (nuevoEstado === 'Recibido en DTHS') update.fechaRecibidoRRHH = ahora2;
          if (nuevoEstado === 'Visto en DTHS') update.fechaVistoRRHH = ahora2;
          if (nuevoEstado === 'En Proceso de Revisión') update.fechaRevision = ahora2;
          return update;
        });
      }
      mostrarExito(`Estado actualizado: ${nuevoEstado}`);
    } catch (err) { setError('Error al actualizar estado'); }
  };

  const abrirFichaPostulante = (postulacion) => {
    setPostulacionSeleccionada(postulacion);
    setModalFichaPostulante(true);
  };

  const mostrarExito = (mensaje) => {
    setSuccess(mensaje);
    setTimeout(() => setSuccess(null), 5000);
  };

  if (loading || loadingData) {
    return (
      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'400px', flexDirection:'column', gap:'1rem' }}>
        <div style={{ width:48, height:48, border:'4px solid #e2e8f0', borderTopColor:'#1e40af', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
        <p style={{ color:'#64748b', fontWeight:500 }}>Cargando sistema de vacantes...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const navItems = [
    { id:'dashboard',    nombre:'Dashboard',   show: permisos.verDashboard },
    { id:'solicitudes',  nombre:'Solicitudes',  show: permisos.crearSolicitud || permisos.aprobarDirectorArea || permisos.aprobarGerenteRRHH || permisos.aprobarDirectorRRHH || permisos.publicarVacante || permisos.crearVacante },
    { id:'vacantes',     nombre:'Vacantes',     show: true },
    { id:'postulaciones',nombre: permisos.validarPostulacionEquipo ? 'Validar Postulaciones' : permisos.gestionarEstadosRRHH ? 'Gestión de Candidatos' : 'Mis Postulaciones', show: permisos.gestionarPostulaciones || permisos.postularse || permisos.validarPostulacionEquipo }
  ].filter(v => v.show);

  const vistaDisponible = navItems.find(n => n.id === vistaActiva) ? vistaActiva : navItems[0]?.id || 'vacantes';

  return (
    <div style={{ minHeight:'100vh', backgroundColor:'#f0f4f8', padding:'1.5rem', fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideRight { from { transform: translateX(120%); opacity:0; } to { transform: translateX(0); opacity:1; } }
        @keyframes fadeUp { from { transform: translateY(10px); opacity:0; } to { transform: translateY(0); opacity:1; } }
        @keyframes toastIn { from { transform: translateY(16px); opacity:0; } to { transform: translateY(0); opacity:1; } }
        .btn-action:hover { opacity:.88; transform:translateY(-1px); }
        .row-hover:hover { background:#f8fafc !important; }
        .kanban-card:hover { box-shadow:0 6px 18px rgba(0,0,0,.12); transform:translateY(-2px); }
        input:focus, select:focus, textarea:focus { outline:none; border-color:#3b82f6 !important; box-shadow:0 0 0 3px rgba(59,130,246,.12); }
      `}</style>

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div style={{ background:'linear-gradient(135deg,#0f172a 0%,#1e3a8a 55%,#2563eb 100%)', borderRadius:'1.25rem', marginBottom:'1.5rem', overflow:'hidden', boxShadow:'0 8px 32px rgba(30,58,138,.25)' }}>
        <div style={{ padding:'2rem 2rem 0', color:'white' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.5rem' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
              <div style={{ width:50, height:50, borderRadius:'0.875rem', background:'rgba(255,255,255,0.15)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid rgba(255,255,255,0.2)' }}>
                <Briefcase size={24} />
              </div>
              <div>
                <h1 style={{ fontSize:'1.5rem', fontWeight:800, margin:0, letterSpacing:'-0.02em' }}>Talento Humano</h1>
                <p style={{ opacity:0.6, margin:0, fontSize:'0.8rem', fontWeight:400, marginTop:2 }}>Sistema de Reclutamiento y Selección</p>
              </div>
            </div>
            <button onClick={cargarDatos} style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.5rem 0.875rem', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'0.5rem', cursor:'pointer', color:'white', fontSize:'0.8rem', fontWeight:600 }}>
              <RefreshCw size={14} /> Actualizar
            </button>
          </div>

          {/* STATS */}
          {permisos.verDashboard && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'0.875rem', marginBottom:'1.5rem' }}>
              {[
                { label:'Solicitudes Pendientes', value: estadisticas.solicitudesPendientes||0, icon:Clock, color:'#fbbf24' },
                { label:'Vacantes Activas', value: estadisticas.vacantesActivas||0, icon:Briefcase, color:'#34d399' },
                { label:'Total Candidatos', value: estadisticas.postulaciones||0, icon:Users, color:'#60a5fa' },
                { label:'En Proceso', value: estadisticas.enProceso||0, icon:Activity, color:'#a78bfa' },
              ].map(s => (
                <div key={s.label} style={{ background:'rgba(255,255,255,0.08)', backdropFilter:'blur(8px)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'0.75rem', padding:'0.875rem 1rem', display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <div style={{ width:36, height:36, borderRadius:'0.625rem', background:`${s.color}22`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <s.icon size={17} style={{ color:s.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize:'1.625rem', fontWeight:800, lineHeight:1, letterSpacing:'-0.04em' }}>{s.value}</div>
                    <div style={{ fontSize:'0.68rem', opacity:0.65, marginTop:2, fontWeight:500 }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* NAV TABS */}
          <div style={{ display:'flex', gap:'0.125rem' }}>
            {navItems.map(vista => (
              <button key={vista.id} onClick={() => setVistaActiva(vista.id)} style={{ padding:'0.75rem 1.25rem', border:'none', background: vistaDisponible===vista.id ? 'white' : 'transparent', color: vistaDisponible===vista.id ? '#1e40af' : 'rgba(255,255,255,0.7)', borderRadius:'0.625rem 0.625rem 0 0', cursor:'pointer', fontWeight:700, fontSize:'0.82rem', transition:'all 0.2s', whiteSpace:'nowrap' }}>
                {vista.nombre}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTENIDO ──────────────────────────────────────────────────── */}
      <div style={{ backgroundColor:'white', borderRadius:'1rem', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', padding:'2rem', border:'1px solid #e9eef5' }}>
        {vistaDisponible==='dashboard'     && permisos.verDashboard && <VistaDashboard estadisticas={estadisticas} permisos={permisos} setModalSolicitud={setModalSolicitud} setModalVacante={setModalVacante} />}
        {vistaDisponible==='solicitudes'   && <VistaSolicitudes solicitudes={solicitudes} user={user} permisos={permisos} setModalSolicitud={setModalSolicitud} aprobarSolicitud={aprobarSolicitud} rechazarSolicitud={rechazarSolicitud} publicarVacante={publicarVacante} cambiarEstadoSolicitud={cambiarEstadoSolicitud} />}
        {vistaDisponible==='vacantes'      && <VistaVacantes vacantes={vacantes} permisos={permisos} setModalVacante={setModalVacante} setVacanteSeleccionada={setVacanteSeleccionada} setModalPostulacion={setModalPostulacion} setModalEditarVacante={setModalEditarVacante} togglePublicacionVacante={togglePublicacionVacante} setModalCerrarVacante={setModalCerrarVacante} />}
        {vistaDisponible==='postulaciones' && <VistaPostulaciones postulaciones={postulaciones} permisos={permisos} cambiarEstadoPostulacion={cambiarEstadoPostulacion} validarPostulacionSupervisor={validarPostulacionSupervisor} user={user} abrirFichaPostulante={abrirFichaPostulante} />}
      </div>

      {/* ── MODALES ────────────────────────────────────────────────────── */}
      {modalSolicitud    && <ModalSolicitud onClose={() => setModalSolicitud(false)} onGuardar={crearSolicitud} departamentos={departamentos} />}
      {modalVacante      && <ModalVacante onClose={() => setModalVacante(false)} onGuardar={crearVacanteDirecta} departamentos={departamentos} />}
      {modalEditarVacante && vacanteSeleccionada && <ModalEditarVacante vacante={vacanteSeleccionada} onClose={() => { setModalEditarVacante(false); setVacanteSeleccionada(null); }} onGuardar={editarVacante} departamentos={departamentos} />}
      {modalPostulacion  && <ModalPostulacionInterna vacante={vacanteSeleccionada} user={user} onClose={() => { setModalPostulacion(false); setVacanteSeleccionada(null); }} onGuardar={postularse} />}
      {modalCerrarVacante && vacanteSeleccionada && <ModalCerrarVacante vacante={vacanteSeleccionada} onClose={() => { setModalCerrarVacante(false); setVacanteSeleccionada(null); }} onGuardar={cerrarVacante} />}
      {modalFichaPostulante && postulacionSeleccionada && (
        <ModalFichaPostulante
          postulacion={postulacionSeleccionada}
          permisos={permisos}
          cambiarEstadoPostulacion={cambiarEstadoPostulacion}
          validarPostulacionSupervisor={validarPostulacionSupervisor}
          user={user}
          onClose={() => { setModalFichaPostulante(false); setPostulacionSeleccionada(null); }}
        />
      )}

      {error   && <Notificacion type="error"   message={error}   onClose={() => setError(null)} />}
      {success && <Notificacion type="success" message={success} onClose={() => setSuccess(null)} />}
    </div>
  );
};

// ============================================================
// VISTA DASHBOARD
// ============================================================
const VistaDashboard = ({ estadisticas, permisos, setModalSolicitud, setModalVacante }) => (
  <div>
    <div style={{ marginBottom:'1.5rem' }}>
      <h2 style={{ fontSize:'1.375rem', fontWeight:800, margin:'0 0 0.25rem', color:'#0f172a' }}>Dashboard</h2>
      <p style={{ color:'#64748b', fontSize:'0.875rem', margin:0 }}>Panel de control de reclutamiento</p>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:'1rem', marginBottom:'2rem' }}>
      <StatCard valor={estadisticas.solicitudesPendientes||0} etiqueta="Solicitudes Pendientes" color="#f59e0b" icono={Clock} />
      <StatCard valor={estadisticas.vacantesActivas||0}       etiqueta="Vacantes Activas"       color="#3b82f6" icono={Briefcase} />
      <StatCard valor={estadisticas.postulaciones||0}         etiqueta="Total Postulaciones"    color="#10b981" icono={Users} />
    </div>
    <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap' }}>
      {permisos.crearSolicitud && <button onClick={() => setModalSolicitud(true)} style={{ ...btnStyle('#1e40af'), padding:'0.75rem 1.25rem' }}><Plus size={15} /> Nueva Solicitud</button>}
      {permisos.crearVacante   && <button onClick={() => setModalVacante(true)}   style={{ ...btnStyle('#059669'), padding:'0.75rem 1.25rem' }}><Plus size={15} /> Nueva Vacante</button>}
    </div>
  </div>
);

const StatCard = ({ valor, etiqueta, color, icono: Icono }) => (
  <div style={{ backgroundColor:'white', padding:'1.25rem', borderRadius:'0.875rem', border:`1px solid ${color}20`, boxShadow:`0 1px 4px ${color}10` }}>
    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.75rem' }}>
      <div style={{ width:32, height:32, borderRadius:'0.5rem', backgroundColor:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <Icono size={16} style={{ color }} />
      </div>
      <span style={{ fontSize:'0.75rem', color:'#64748b', fontWeight:600 }}>{etiqueta}</span>
    </div>
    <div style={{ fontSize:'2rem', fontWeight:800, color, letterSpacing:'-0.05em' }}>{valor}</div>
  </div>
);

// ============================================================
// VISTA SOLICITUDES
// ============================================================
const VistaSolicitudes = ({ solicitudes, user, permisos, setModalSolicitud, aprobarSolicitud, rechazarSolicitud, publicarVacante, cambiarEstadoSolicitud }) => {
  const [filtro, setFiltro] = useState('todas');
  const [solicitudEnProceso, setSolicitudEnProceso] = useState(null);
  const filtradas = solicitudes.filter(s => filtro==='todas' || s.estado?.toLowerCase().includes(filtro.toLowerCase()));

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
        <div>
          <h2 style={{ fontSize:'1.375rem', fontWeight:800, margin:'0 0 0.25rem', color:'#0f172a' }}>Gestión de Solicitudes</h2>
          <p style={{ color:'#64748b', fontSize:'0.875rem', margin:0 }}>Flujo de aprobación jerárquico</p>
        </div>
        <div style={{ display:'flex', gap:'0.75rem', alignItems:'center' }}>
          <select value={filtro} onChange={e => setFiltro(e.target.value)} style={selectSt}>
            <option value="todas">Todas</option>
            <option value="pendiente">Pendientes</option>
            <option value="aprobada">Aprobadas</option>
            <option value="publicada">Publicadas</option>
            <option value="rechazada">Rechazadas</option>
          </select>
          {permisos.crearSolicitud && (
            <button onClick={() => setModalSolicitud(true)} style={{ ...btnStyle('#1e40af'), padding:'0.55rem 1rem', fontSize:'0.8rem' }}>
              <Plus size={14} /> Nueva Solicitud
            </button>
          )}
        </div>
      </div>
      <div style={{ display:'grid', gap:'1rem' }}>
        {filtradas.length===0
          ? <EmptyState icono={FileText} titulo="No hay solicitudes" descripcion="No se encontraron solicitudes con ese filtro" />
          : filtradas.map(s => <TarjetaSolicitud key={s.id} solicitud={s} user={user} permisos={permisos} aprobarSolicitud={aprobarSolicitud} rechazarSolicitud={rechazarSolicitud} publicarVacante={publicarVacante} solicitudEnProceso={solicitudEnProceso} setSolicitudEnProceso={setSolicitudEnProceso} cambiarEstadoSolicitud={cambiarEstadoSolicitud} />)}
      </div>
    </div>
  );
};

const TarjetaSolicitud = ({ solicitud, user, permisos, aprobarSolicitud, rechazarSolicitud, publicarVacante, solicitudEnProceso, setSolicitudEnProceso, cambiarEstadoSolicitud }) => {
  const [expandida, setExpandida] = useState(false);
  const [panelRRHH, setPanelRRHH] = useState(false);
  const [nuevoEstadoRRHH, setNuevoEstadoRRHH] = useState('');
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [mostrarRechazo, setMostrarRechazo] = useState(false);
  const rolNorm = user?.role?.toLowerCase().replace(/\s+/g, '_');
  const esRRHH = ['dths_director','dths_gerente','dths_analista','dths_coordinador'].includes(rolNorm);
  const rechazada = solicitud.estado==='Rechazada';

  const puedeAprobarDir   = rolNorm==='director'       && solicitud.estado==='Pendiente';
  const puedeAprobarGRRHH = rolNorm==='dths_gerente'   && solicitud.estado==='Aprobada por Director de Área';
  const puedeAprobarDRRHH = rolNorm==='dths_director'  && solicitud.estado==='Aprobada por Gerente DTHS';
  const puedePublicar     = ['dths_director','dths_gerente','dths_analista','dths_coordinador'].includes(rolNorm) && solicitud.estado==='Aprobada por Director DTHS';
  const hayAccion = puedeAprobarDir||puedeAprobarGRRHH||puedeAprobarDRRHH||puedePublicar;

  const handleAcc = async (accion, id, com='') => {
    setSolicitudEnProceso(id);
    try {
      if      (accion==='dir')   await aprobarSolicitud(id,'director-area');
      else if (accion==='gdths') await aprobarSolicitud(id,'gerente-dths');
      else if (accion==='ddths') await aprobarSolicitud(id,'director-dths');
      else if (accion==='pub')   await publicarVacante(id);
      else if (accion==='rec')   await rechazarSolicitud(id,com);
    } finally { setSolicitudEnProceso(null); }
  };

  const ordenEstados = ['Pendiente','Aprobada por Director de Área','Aprobada por Gerente DTHS','Aprobada por Director DTHS','Publicada'];
  const idxActual = rechazada ? -1 : ordenEstados.indexOf(solicitud.estado);
  const fmtFecha = (f) => { try { return f ? new Date(f).toLocaleString('es-DO',{ day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : null; } catch { return f; } };

  const pasos = [
    { label:'Solicitante',      sublabel:'Crea la solicitud',      icono:<FileText size={16} />, actor:solicitud.solicitante,                fecha:solicitud.fechaSolicitud,              estadoRef:'Pendiente',                    color:'#64748b' },
    { label:'Director de Área', sublabel:'Primera aprobación',     icono:<UserRound size={16} />, actor:solicitud.aprobadorDirectorArea||null, fecha:solicitud.fechaAprobacionDirector||null,estadoRef:'Aprobada por Director de Área', color:'#0891b2', pendiente:idxActual===0 },
    { label:'Gerente DTHS',     sublabel:'Revisión RRHH',          icono:<UserRound size={16} />, actor:solicitud.aprobadorGerenteRRHH||null,  fecha:solicitud.fechaAprobacionGerenteRRHH||null,estadoRef:'Aprobada por Gerente DTHS', color:'#059669', pendiente:idxActual===1 },
    { label:'Director DTHS',    sublabel:'Autorización final',     icono:<UserRound size={16} />, actor:solicitud.aprobadorDirectorRRHH||null, fecha:solicitud.fechaAprobacionDirectorRRHH||null,estadoRef:'Aprobada por Director DTHS',color:'#1e40af', pendiente:idxActual===2 },
    { label:'Publicación',      sublabel:'Vacante activa',         icono:<Megaphone size={16} />, actor:solicitud.publicadoPor||null,          fecha:solicitud.fechaPublicacion||null,       estadoRef:'Publicada',                    color:'#7c3aed', pendiente:idxActual===3 },
  ];

  return (
    <div style={{ border: rechazada ? '1px solid #fecaca' : '1px solid #e9eef5', borderRadius:'0.875rem', backgroundColor: rechazada ? '#fff8f8' : 'white', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', overflow:'hidden' }}>
      <div style={{ padding:'1.25rem 1.25rem 0' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.75rem' }}>
          <div style={{ flex:1 }}>
            <h3 style={{ fontSize:'1.05rem', fontWeight:700, margin:'0 0 0.375rem', color:'#0f172a' }}>{solicitud.cargo}</h3>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'1.25rem', fontSize:'0.78rem', color:'#64748b' }}>
              <span style={{ display:'flex', alignItems:'center', gap:'0.25rem' }}><User size={12} />{solicitud.solicitante}</span>
              <span style={{ display:'flex', alignItems:'center', gap:'0.25rem' }}><Building2 size={12} />{solicitud.departamento}</span>
              <span style={{ display:'flex', alignItems:'center', gap:'0.25rem' }}><DollarSign size={12} />RD${(solicitud.salarioMin||0).toLocaleString()} — RD${(solicitud.salarioMax||0).toLocaleString()}</span>
            </div>
          </div>
          <EstadoBadgeLegacy estado={solicitud.estado} />
        </div>
      </div>

      {/* STEPPER */}
      <div style={{ padding:'1rem 1.25rem', backgroundColor: rechazada ? '#fef2f2' : '#f8fafc', borderTop:'1px solid #f1f5f9', borderBottom:'1px solid #f1f5f9' }}>
        {rechazada ? (
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.625rem 0.875rem', backgroundColor:'#fee2e2', borderRadius:'0.625rem', border:'1px solid #fecaca' }}>
            <XCircle size={18} style={{ color:'#dc2626', flexShrink:0 }} />
            <div>
              <span style={{ fontSize:'0.8rem', fontWeight:700, color:'#991b1b' }}>Solicitud Rechazada</span>
              {solicitud.comentariosRechazo && <p style={{ fontSize:'0.75rem', color:'#b91c1c', margin:'0.125rem 0 0' }}>{solicitud.comentariosRechazo}</p>}
            </div>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'flex-start', gap:0, overflowX:'auto', paddingBottom:'0.25rem' }}>
            {pasos.map((paso, i) => {
              const completado = idxActual>=i;
              const esActual   = paso.pendiente;
              const pendFuturo = idxActual<i && !rechazada;
              return (
                <React.Fragment key={paso.label}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', minWidth:110, flex:1 }}>
                    <div style={{ width:40, height:40, borderRadius:'50%', backgroundColor: completado ? paso.color : esActual ? `${paso.color}20` : '#f1f5f9', border:`2px solid ${completado ? paso.color : esActual ? paso.color : '#e2e8f0'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', boxShadow: esActual ? `0 0 0 4px ${paso.color}20` : 'none', transition:'all 0.3s', position:'relative', zIndex:1 }}>
                      {completado ? <CheckCircle size={18} style={{ color:'white' }} /> : <span style={{ opacity: pendFuturo ? 0.35 : 1 }}>{paso.icono}</span>}
                    </div>
                    <div style={{ textAlign:'center', marginTop:'0.5rem', maxWidth:100 }}>
                      <div style={{ fontSize:'0.72rem', fontWeight:700, color: completado ? paso.color : pendFuturo ? '#cbd5e1' : '#374151', lineHeight:1.2 }}>{paso.label}</div>
                      <div style={{ fontSize:'0.62rem', color:'#94a3b8', marginTop:'0.1rem', lineHeight:1.2 }}>{paso.sublabel}</div>
                      {completado && paso.actor && (
                        <div style={{ marginTop:'0.35rem', padding:'0.2rem 0.4rem', backgroundColor:'white', borderRadius:'0.375rem', border:`1px solid ${paso.color}30`, display:'inline-block' }}>
                          <div style={{ fontSize:'0.62rem', fontWeight:700, color:paso.color, whiteSpace:'nowrap' }}>{paso.actor}</div>
                          {paso.fecha && <div style={{ fontSize:'0.57rem', color:'#94a3b8', whiteSpace:'nowrap', marginTop:'0.05rem' }}>{fmtFecha(paso.fecha)}</div>}
                        </div>
                      )}
                      {esActual && (
                        <div style={{ marginTop:'0.35rem', padding:'0.18rem 0.45rem', backgroundColor:paso.color, borderRadius:'9999px', display:'inline-block' }}>
                          <span style={{ fontSize:'0.58rem', fontWeight:700, color:'white', whiteSpace:'nowrap' }}><Clock size={10} style={{ verticalAlign:'-1px', marginRight:'0.2rem' }} />Pendiente</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {i < pasos.length-1 && <div style={{ flex:'0 0 20px', height:2, backgroundColor: idxActual>i ? pasos[i+1].color : '#e2e8f0', marginTop:19, transition:'background 0.3s', borderRadius:2 }} />}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* Detalles expandibles */}
      <div style={{ padding:'0.75rem 1.25rem' }}>
        <button onClick={() => setExpandida(!expandida)} style={{ background:'none', border:'none', cursor:'pointer', color:'#1e40af', fontSize:'0.78rem', fontWeight:700, display:'flex', alignItems:'center', gap:'0.25rem', padding:0, marginBottom: expandida ? '0.75rem' : 0 }}>
          {expandida ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {expandida ? 'Ocultar' : 'Ver'} descripción y justificación
        </button>
        {expandida && (
          <div style={{ backgroundColor:'#f8fafc', borderRadius:'0.5rem', padding:'0.875rem', display:'grid', gap:'0.625rem', fontSize:'0.8rem', border:'1px solid #f1f5f9' }}>
            {solicitud.descripcion   && <div><strong style={{ color:'#374151' }}>Descripción:</strong>   <span style={{ color:'#64748b' }}>{solicitud.descripcion}</span></div>}
            {solicitud.justificacion && <div><strong style={{ color:'#374151' }}>Justificación:</strong> <span style={{ color:'#64748b' }}>{solicitud.justificacion}</span></div>}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', paddingTop:'0.375rem', borderTop:'1px solid #f1f5f9' }}>
              <div><strong>Modalidad:</strong> {solicitud.modalidad||'Presencial'}</div>
              <div><strong>Prioridad:</strong> {solicitud.prioridad||'Media'}</div>
            </div>
          </div>
        )}
      </div>

      {/* Botones acción flujo */}
      {hayAccion && (
        <div style={{ padding:'0.875rem 1.25rem', borderTop:'1px solid #f1f5f9', backgroundColor:'#fafafa', display:'flex', gap:'0.625rem', flexWrap:'wrap', alignItems:'center' }}>
          <span style={{ fontSize:'0.75rem', color:'#64748b', fontWeight:700, marginRight:'0.25rem' }}>Tu acción:</span>
          {puedeAprobarDir   && <button onClick={() => handleAcc('dir',solicitud.id)}   disabled={solicitudEnProceso===solicitud.id} style={btnStyle('#0891b2')}><CheckCircle size={13} /> Aprobar — Director de Área</button>}
          {puedeAprobarGRRHH && <button onClick={() => handleAcc('gdths',solicitud.id)} disabled={solicitudEnProceso===solicitud.id} style={btnStyle('#059669')}><CheckCircle size={13} /> Aprobar — Gerente DTHS</button>}
          {puedeAprobarDRRHH && <button onClick={() => handleAcc('ddths',solicitud.id)} disabled={solicitudEnProceso===solicitud.id} style={btnStyle('#1e40af')}><CheckCircle size={13} /> Autorizar — Director DTHS</button>}
          {(puedeAprobarDir||puedeAprobarGRRHH||puedeAprobarDRRHH) && !mostrarRechazo && (
            <button onClick={() => setMostrarRechazo(true)} style={btnStyle('#dc2626',true)}><XCircle size={13} /> Rechazar</button>
          )}
          {puedePublicar && <button onClick={() => handleAcc('pub',solicitud.id)} disabled={solicitudEnProceso===solicitud.id} style={btnStyle('#7c3aed')}><Send size={13} /> Publicar Vacante</button>}
          {solicitudEnProceso===solicitud.id && <div style={{ width:16, height:16, border:'2px solid #e2e8f0', borderTopColor:'#1e40af', borderRadius:'50%', animation:'spin 0.7s linear infinite', marginLeft:'0.25rem' }} />}
        </div>
      )}

      {/* Panel rechazo inline */}
      {mostrarRechazo && (
        <div style={{ padding:'0.875rem 1.25rem', borderTop:'1px solid #fecaca', backgroundColor:'#fef2f2' }}>
          <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#991b1b', marginBottom:'0.5rem', display:'flex', alignItems:'center', gap:'0.375rem' }}><XCircle size={13} /> Motivo del rechazo</div>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <input value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)} placeholder="Escribe el motivo..." style={{ flex:1, padding:'0.5rem 0.75rem', border:'1px solid #fca5a5', borderRadius:'0.375rem', fontSize:'0.8rem' }} autoFocus />
            <button onClick={async () => { if (motivoRechazo.trim()) { await handleAcc('rec',solicitud.id,motivoRechazo); setMostrarRechazo(false); setMotivoRechazo(''); } }} disabled={!motivoRechazo.trim()||solicitudEnProceso===solicitud.id} style={{ ...btnStyle('#dc2626'), opacity: motivoRechazo.trim() ? 1 : 0.5 }}>Confirmar</button>
            <button onClick={() => { setMostrarRechazo(false); setMotivoRechazo(''); }} style={btnStyle('#6b7280',true)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Panel RRHH */}
      {esRRHH && !rechazada && (
        <div style={{ borderTop:'1px solid #f1f5f9' }}>
          <button onClick={() => setPanelRRHH(!panelRRHH)} style={{ width:'100%', padding:'0.625rem 1.25rem', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:'0.75rem', color:'#64748b', fontWeight:700 }}>
            <span style={{ display:'flex', alignItems:'center', gap:'0.375rem' }}><Edit size={12} /> Gestión RRHH — Cambiar estado o rechazar</span>
            {panelRRHH ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {panelRRHH && (
            <div style={{ padding:'0.875rem 1.25rem', backgroundColor:'#fafafa', display:'grid', gap:'0.875rem', borderTop:'1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize:'0.75rem', fontWeight:700, color:'#374151', marginBottom:'0.5rem' }}>Cambiar estado manualmente</div>
                <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                  <select value={nuevoEstadoRRHH} onChange={e => setNuevoEstadoRRHH(e.target.value)} style={{ flex:1, minWidth:200, padding:'0.5rem 0.75rem', border:'1px solid #e2e8f0', borderRadius:'0.375rem', fontSize:'0.8rem', backgroundColor:'white' }}>
                    <option value="">Seleccionar estado...</option>
                    {['Pendiente','Aprobada por Director de Área','Aprobada por Gerente DTHS','Aprobada por Director DTHS','Publicada'].filter(e => e!==solicitud.estado).map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                  <button onClick={async () => { if (!nuevoEstadoRRHH) return; setSolicitudEnProceso(solicitud.id); try { await cambiarEstadoSolicitud(solicitud.id,nuevoEstadoRRHH); setNuevoEstadoRRHH(''); setPanelRRHH(false); } finally { setSolicitudEnProceso(null); } }} disabled={!nuevoEstadoRRHH||solicitudEnProceso===solicitud.id} style={{ ...btnStyle('#1e40af'), opacity: nuevoEstadoRRHH ? 1 : 0.5 }}><CheckCircle size={13} /> Aplicar</button>
                </div>
                <div style={{ fontSize:'0.7rem', color:'#94a3b8', marginTop:'0.375rem' }}>Estado actual: <strong style={{ color:'#374151' }}>{solicitud.estado}</strong></div>
              </div>
              {solicitud.estado!=='Rechazada' && (
                <div style={{ paddingTop:'0.75rem', borderTop:'1px solid #f1f5f9' }}>
                  <div style={{ fontSize:'0.75rem', fontWeight:700, color:'#dc2626', marginBottom:'0.5rem', display:'flex', alignItems:'center', gap:'0.375rem' }}><XCircle size={12} /> Rechazar en esta etapa</div>
                  <div style={{ display:'flex', gap:'0.5rem' }}>
                    <input value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)} placeholder="Motivo del rechazo..." style={{ flex:1, padding:'0.5rem 0.75rem', border:'1px solid #fca5a5', borderRadius:'0.375rem', fontSize:'0.8rem' }} />
                    <button onClick={async () => { if (!motivoRechazo.trim()) return; setSolicitudEnProceso(solicitud.id); try { await rechazarSolicitud(solicitud.id,motivoRechazo); setMotivoRechazo(''); setPanelRRHH(false); } finally { setSolicitudEnProceso(null); } }} disabled={!motivoRechazo.trim()||solicitudEnProceso===solicitud.id} style={{ ...btnStyle('#dc2626'), opacity: motivoRechazo.trim() ? 1 : 0.5 }}><XCircle size={13} /> Rechazar</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// VISTA VACANTES
// ============================================================
const VistaVacantes = ({ vacantes, permisos, setModalVacante, setVacanteSeleccionada, setModalPostulacion, setModalEditarVacante, togglePublicacionVacante, setModalCerrarVacante }) => (
  <div>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
      <div>
        <h2 style={{ fontSize:'1.375rem', fontWeight:800, margin:'0 0 0.25rem', color:'#0f172a' }}>Vacantes Publicadas</h2>
        <p style={{ color:'#64748b', fontSize:'0.875rem', margin:0 }}>{vacantes.length} oportunidades activas</p>
      </div>
      {permisos.crearVacante && (
        <button onClick={() => setModalVacante(true)} style={{ ...btnStyle('#059669'), padding:'0.55rem 1rem', fontSize:'0.8rem' }}>
          <Plus size={14} /> Nueva Vacante
        </button>
      )}
    </div>
    <div style={{ display:'grid', gap:'1rem' }}>
      {vacantes.length===0
        ? <EmptyState icono={Briefcase} titulo="No hay vacantes" descripcion="No hay vacantes activas publicadas" />
        : vacantes.map(v => <TarjetaVacante key={v.id} vacante={v} permisos={permisos} setVacanteSeleccionada={setVacanteSeleccionada} setModalPostulacion={setModalPostulacion} setModalEditarVacante={setModalEditarVacante} togglePublicacionVacante={togglePublicacionVacante} setModalCerrarVacante={setModalCerrarVacante} />)}
    </div>
  </div>
);

const TarjetaVacante = ({ vacante, permisos, setVacanteSeleccionada, setModalPostulacion, setModalEditarVacante, togglePublicacionVacante, setModalCerrarVacante }) => (
  <div style={{ border:'1px solid #e9eef5', borderRadius:'0.875rem', padding:'1.25rem', backgroundColor:'white', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.875rem' }}>
      <div style={{ flex:1 }}>
        <h3 style={{ fontSize:'1.1rem', fontWeight:700, margin:'0 0 0.375rem', color:'#0f172a' }}>{vacante.titulo}</h3>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'1rem', fontSize:'0.8rem', color:'#64748b' }}>
          <span style={{ display:'flex', alignItems:'center', gap:'0.25rem' }}><MapPin size={13} />{vacante.departamento}</span>
          <span style={{ display:'flex', alignItems:'center', gap:'0.25rem' }}><Users size={13} />{vacante.totalPostulaciones||0} postulaciones</span>
          <span style={{ display:'flex', alignItems:'center', gap:'0.25rem' }}><DollarSign size={13} />RD${(vacante.salarioMinimo||0).toLocaleString()} — RD${(vacante.salarioMaximo||0).toLocaleString()}</span>
        </div>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem', alignItems:'flex-end' }}>
        <EstadoBadgeLegacy estado={vacante.estado} />
        {vacante.PublicaEnPortal ? <span style={{ fontSize:'0.7rem', color:'#0891b2', backgroundColor:'#ecfeff', padding:'0.15rem 0.5rem', borderRadius:'9999px', fontWeight:600 }}><Globe size={12} style={{ verticalAlign:'-2px', marginRight:'0.25rem' }} />Portal Público</span> : null}
      </div>
    </div>
    {vacante.descripcion && <p style={{ color:'#64748b', marginBottom:'0.875rem', lineHeight:1.5, fontSize:'0.8rem' }}>{vacante.descripcion}</p>}
    <div style={{ display:'flex', justifyContent:'flex-end', gap:'0.5rem', flexWrap:'wrap', paddingTop:'0.875rem', borderTop:'1px solid #f8fafc' }}>
      {permisos.crearVacante && (
        <>
          <button onClick={() => { setVacanteSeleccionada(vacante); setModalEditarVacante(true); }} style={btnStyle('#f59e0b',true)}><Edit size={13} /> Editar</button>
          <button onClick={() => togglePublicacionVacante(vacante.id, !vacante.PublicaEnPortal)} style={btnStyle(vacante.PublicaEnPortal ? '#dc2626' : '#10b981',true)}>
            {vacante.PublicaEnPortal ? <><X size={13} /> Despublicar</> : <><CheckCircle size={13} /> Portal</>}
          </button>
          {vacante.estado==='Activa' && <button onClick={() => { setVacanteSeleccionada(vacante); setModalCerrarVacante(true); }} style={btnStyle('#6b7280',true)}><XCircle size={13} /> Cerrar</button>}
        </>
      )}
      {permisos.postularse && vacante.estado==='Activa' && (
        <button onClick={() => { setVacanteSeleccionada(vacante); setModalPostulacion(true); }} style={btnStyle('#1e40af')}><Send size={13} /> Postularme</button>
      )}
    </div>
  </div>
);

// ============================================================
// VISTA POSTULACIONES — MEJORADA con Pipeline + Tabla
// ============================================================
const VistaPostulaciones = ({ postulaciones, permisos, cambiarEstadoPostulacion, validarPostulacionSupervisor, user, abrirFichaPostulante }) => {
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [vistaMode, setVistaMode] = useState(permisos.gestionarEstadosRRHH ? 'pipeline' : 'tabla');
  const [filtroVacante, setFiltroVacante] = useState('');

  const vacantesUnicas = [...new Set(postulaciones.map(p => p.cargoVacante).filter(Boolean))];
  const pendientesValidacion = postulaciones.filter(p =>
  p.estado === 'Pendiente Validación Supervisor' ||
  p.estado === 'Pendiente Aprobación Gerente'    ||
  p.estado === 'Pendiente Aprobación Director'
).length;

  const filtradas = postulaciones.filter(p => {
    const estadoNorm = normalizarEstado(p.estado);
    const mE = !filtroEstado || estadoNorm === filtroEstado || p.estado === filtroEstado;
    const mB = !busqueda || [p.nombreCandidato,p.nombre,p.email].some(f => f?.toLowerCase().includes(busqueda.toLowerCase()));
    const mV = !filtroVacante || p.cargoVacante===filtroVacante;
    return mE && mB && mV;
  });

  const titulo = permisos.validarPostulacionEquipo ? 'Validar Postulaciones de Mi Equipo' : permisos.gestionarEstadosRRHH ? 'Gestión de Candidatos' : 'Mis Postulaciones';

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
        <div>
          <h2 style={{ fontSize:'1.375rem', fontWeight:800, margin:'0 0 0.25rem', color:'#0f172a' }}>{titulo}</h2>
          <p style={{ color:'#64748b', fontSize:'0.875rem', margin:0 }}>{filtradas.length} candidato{filtradas.length!==1?'s':''} encontrado{filtradas.length!==1?'s':''}</p>
        </div>
        {permisos.gestionarEstadosRRHH && (
          <div style={{ display:'flex', gap:'0.25rem', border:'1px solid #e2e8f0', borderRadius:'0.625rem', overflow:'hidden', backgroundColor:'#f8fafc' }}>
            {[{ id:'pipeline', label:'Pipeline', icon:LayoutGrid },{ id:'tabla', label:'Tabla', icon:List }].map(v => (
              <button key={v.id} onClick={() => setVistaMode(v.id)} style={{ padding:'0.5rem 0.875rem', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:'0.375rem', fontSize:'0.78rem', fontWeight:700, backgroundColor: vistaMode===v.id ? 'white' : 'transparent', color: vistaMode===v.id ? '#1e40af' : '#6b7280', boxShadow: vistaMode===v.id ? '0 1px 3px rgba(0,0,0,.08)' : 'none', transition:'all 0.15s' }}>
                <v.icon size={13} /> {v.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Alerta pendientes supervisor */}
      {permisos.validarPostulacionEquipo && pendientesValidacion>0 && (
        <div style={{ backgroundColor:'#fffbeb', border:'1px solid #fcd34d', borderRadius:'0.75rem', padding:'0.875rem 1rem', marginBottom:'1.25rem', display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <div style={{ width:36, height:36, borderRadius:'50%', backgroundColor:'#fef3c7', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <AlertCircle size={18} style={{ color:'#d97706' }} />
          </div>
          <div>
            <strong style={{ color:'#92400e', fontSize:'0.875rem' }}>{pendientesValidacion} postulación(es) esperando tu validación</strong>
            <p style={{ color:'#92400e', fontSize:'0.75rem', margin:'0.125rem 0 0' }}>Tu aprobación es necesaria para continuar el proceso.</p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display:'flex', gap:'0.75rem', marginBottom:'1.25rem', flexWrap:'wrap', alignItems:'center', padding:'0.875rem 1rem', background:'#f8fafc', borderRadius:'0.75rem', border:'1px solid #e9eef5' }}>
        <div style={{ position:'relative', flex:1, minWidth:200 }}>
          <Search size={15} style={{ position:'absolute', left:'0.75rem', top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }} />
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar candidato o email..." style={{ width:'100%', padding:'0.6rem 0.75rem 0.6rem 2.25rem', border:'1px solid #e2e8f0', borderRadius:'0.5rem', fontSize:'0.82rem', background:'white', transition:'all 0.2s' }} />
        </div>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={selectSt}>
          <option value="">Todos los estados</option>
          {PIPELINE.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          <option value="Pendiente Validación Supervisor">Pendiente Validación</option>
          <option value="Rechazada por Supervisor">Rechazado Supervisor</option>
        </select>
        <select value={filtroVacante} onChange={e => setFiltroVacante(e.target.value)} style={{ ...selectSt, maxWidth:220 }}>
          <option value="">Todas las vacantes</option>
          {vacantesUnicas.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        {(busqueda||filtroEstado||filtroVacante) && (
          <button onClick={() => { setBusqueda(''); setFiltroEstado(''); setFiltroVacante(''); }} style={{ display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.6rem 0.875rem', border:'1px solid #e2e8f0', borderRadius:'0.5rem', fontSize:'0.8rem', background:'white', cursor:'pointer', color:'#64748b', fontWeight:600 }}>
            <X size={13} /> Limpiar
          </button>
        )}
      </div>

      {/* ── VISTA PIPELINE ────────────────────────────────────── */}
      {vistaMode==='pipeline' && permisos.gestionarEstadosRRHH && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(6,minmax(185px,1fr))', gap:'0.875rem', overflowX:'auto', paddingBottom:'0.5rem' }}>
          {PIPELINE.map(col => {
            // FIX: usar normalizarEstado para comparar
            const items = filtradas.filter(p => normalizarEstado(p.estado) === col.id);
            return (
              <div key={col.id} style={{ background:'white', borderRadius:'1rem', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,.06)', border:'1px solid #e9eef5', display:'flex', flexDirection:'column' }}>
                <div style={{ padding:'0.875rem', borderBottom:`3px solid ${col.color}`, background:col.light }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.3rem' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
                      <col.icon size={13} style={{ color:col.color }} />
                      <span style={{ fontSize:'0.68rem', fontWeight:800, color:col.color, textTransform:'uppercase', letterSpacing:'0.06em' }}>{col.label}</span>
                    </div>
                    <span style={{ background:col.color, color:'white', borderRadius:'9999px', padding:'0.1rem 0.45rem', fontSize:'0.65rem', fontWeight:800 }}>{items.length}</span>
                  </div>
                  <p style={{ fontSize:'0.62rem', color:'#94a3b8', margin:0, fontWeight:500 }}>{col.desc}</p>
                </div>
                <div style={{ padding:'0.625rem', display:'flex', flexDirection:'column', gap:'0.5rem', flex:1, overflowY:'auto', maxHeight:500 }}>
                  {items.length===0 ? (
                    <div style={{ textAlign:'center', padding:'1.5rem 0.5rem', color:'#cbd5e1', fontSize:'0.7rem' }}>
                      <Circle size={22} style={{ margin:'0 auto 0.4rem', opacity:0.3 }} />Sin candidatos
                    </div>
                  ) : items.map(p => (
                    <KanbanCard key={p.id} p={p} col={col} onOpen={() => abrirFichaPostulante(p)} onAdvance={cambiarEstadoPostulacion} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── VISTA TABLA ───────────────────────────────────────── */}
      {vistaMode==='tabla' && (
        filtradas.length===0 ? (
          <EmptyState icono={Users} titulo="No hay postulaciones" descripcion="No se encontraron candidatos con los filtros aplicados" />
        ) : (
          <div style={{ border:'1px solid #e9eef5', borderRadius:'0.875rem', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor:'#f8fafc', borderBottom:'2px solid #f1f5f9' }}>
                    {['Candidato','Vacante','Tipo','Estado','Progreso','Docs','Fecha','Acciones'].map(h => (
                      <th key={h} style={{ padding:'0.875rem 1rem', textAlign:'left', fontSize:'0.68rem', fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((p,i) => (
                    <FilaPostulacion key={p.id} postulacion={p} permisos={permisos} cambiarEstadoPostulacion={cambiarEstadoPostulacion} validarPostulacionSupervisor={validarPostulacionSupervisor} abrirFichaPostulante={abrirFichaPostulante} isOdd={i%2===1} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* Vista colaborador — mis postulaciones */}
      {!permisos.gestionarEstadosRRHH && !permisos.validarPostulacionEquipo && (
        <div style={{ display:'grid', gap:'1rem' }}>
          {filtradas.map(p => <TarjetaEstadoCandidato key={p.id} postulacion={p} onVerDetalle={() => abrirFichaPostulante(p)} />)}
        </div>
      )}
    </div>
  );
};

// ── KANBAN CARD ───────────────────────────────────────────────────────────────
const KanbanCard = ({ p, col, onOpen, onAdvance }) => {
  // FIX: usar normalizarEstado para determinar el siguiente estado
  const nextEstado = PROXY_NEXT[normalizarEstado(p.estado)];
  const initials = (p.nombreCandidato||p.nombre||'?').split(' ').map(n=>n[0]).slice(0,2).join('').toUpperCase();
  return (
    <div onClick={onOpen} className="kanban-card" style={{ background:'white', border:'1px solid #f1f5f9', borderRadius:'0.75rem', padding:'0.875rem', cursor:'pointer', transition:'all 0.2s', position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', left:0, top:0, bottom:0, width:3, background:col.color }} />
      <div style={{ paddingLeft:'0.25rem' }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:'0.5rem', marginBottom:'0.5rem' }}>
          <div style={{ width:28, height:28, borderRadius:'50%', background:`${col.color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.62rem', fontWeight:800, color:col.color, flexShrink:0 }}>{initials}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'0.76rem', fontWeight:700, color:'#0f172a', lineHeight:1.3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.nombreCandidato||p.nombre}</div>
            <div style={{ fontSize:'0.65rem', color:'#94a3b8', marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.email}</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.3rem', marginBottom:'0.5rem' }}>
          <Briefcase size={10} style={{ color:'#94a3b8', flexShrink:0 }} />
          <span style={{ fontSize:'0.66rem', color:'#64748b', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.cargoVacante}</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.375rem' }}>
          <TipoBadge tipo={p.tipoPostulante} small />
          {nextEstado && (
            <button onClick={e => { e.stopPropagation(); onAdvance(p.id,nextEstado); }} style={{ display:'flex', alignItems:'center', gap:'0.2rem', padding:'0.18rem 0.45rem', background:col.light, border:`1px solid ${col.color}40`, borderRadius:'0.375rem', cursor:'pointer', fontSize:'0.6rem', fontWeight:700, color:col.color, transition:'all 0.15s' }}>
              <ArrowRight size={9} /> Avanzar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── FILA TABLA ────────────────────────────────────────────────────────────────
const FilaPostulacion = ({ postulacion:p, permisos, cambiarEstadoPostulacion, validarPostulacionSupervisor, abrirFichaPostulante, isOdd }) => {
  const cfg = getEstadoConfig(p.estado);
  // FIX: usar normalizarEstado para determinar el siguiente estado y el progreso
  const nextEstado = PROXY_NEXT[normalizarEstado(p.estado)];
  const puedeValidar = permisos.validarPostulacionEquipo && (
  p.estado === 'Pendiente Validación Supervisor' ||
  p.estado === 'Pendiente Aprobación Gerente'    ||
  p.estado === 'Pendiente Aprobación Director'
);
  const pct = {
    'Enviado':16,
    'Recibido en DTHS':33,
    'Visto en DTHS':50,
    'En Proceso de Revisión':66,
    'Postulación Aprobada':100,
    'Aprobación Declinada':100
  }[normalizarEstado(p.estado)] || 0;
  const docCount = Object.keys(p.documentos||{}).length;
  const tdS = { padding:'0.875rem 1rem', fontSize:'0.8rem', color:'#374151', borderBottom:'1px solid #f8fafc', backgroundColor: isOdd ? '#fafbfc' : 'white' };
  const initials = (p.nombreCandidato||p.nombre||'?').split(' ').map(n=>n[0]).slice(0,2).join('').toUpperCase();

  return (
    <tr className="row-hover">
      <td style={tdS}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.625rem' }}>
          <div style={{ width:32, height:32, borderRadius:'50%', background:`${cfg.color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.65rem', fontWeight:800, color:cfg.color, flexShrink:0 }}>{initials}</div>
          <div>
            <div style={{ fontWeight:700, color:'#0f172a', fontSize:'0.83rem', lineHeight:1.2 }}>{p.nombreCandidato||p.nombre}</div>
            <div style={{ color:'#94a3b8', fontSize:'0.72rem', marginTop:1 }}>{p.email}</div>
          </div>
        </div>
      </td>
      <td style={tdS}><span style={{ fontSize:'0.78rem', color:'#374151', fontWeight:500 }}>{p.cargoVacante}</span></td>
      <td style={tdS}><TipoBadge tipo={p.tipoPostulante} /></td>
      <td style={tdS}><EstadoBadgeNew cfg={cfg} label={p.estado} /></td>
      <td style={{ ...tdS, minWidth:120 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <div style={{ flex:1, height:5, background:'#f1f5f9', borderRadius:'9999px', overflow:'hidden' }}>
            <div style={{ width:`${pct}%`, height:'100%', background: normalizarEstado(p.estado)==='Aprobación Declinada'?'#dc2626':cfg.color, borderRadius:'9999px', transition:'width 0.4s' }} />
          </div>
          <span style={{ fontSize:'0.65rem', color:'#94a3b8', fontWeight:700, minWidth:28 }}>{pct}%</span>
        </div>
      </td>
      <td style={tdS}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.3rem' }}>
          <Paperclip size={12} style={{ color:'#94a3b8' }} />
          <span style={{ fontSize:'0.75rem', color:'#64748b', fontWeight:600 }}>{docCount > 0 ? `${docCount} docs` : '—'}</span>
        </div>
      </td>
      <td style={{ ...tdS, whiteSpace:'nowrap' }}>
        <span style={{ fontSize:'0.73rem', color:'#94a3b8' }}>{p.fechaPostulacion ? new Date(p.fechaPostulacion).toLocaleDateString('es-DO',{ day:'2-digit', month:'short', year:'2-digit' }) : '—'}</span>
      </td>
      <td style={tdS}>
        <div style={{ display:'flex', gap:'0.375rem', alignItems:'center' }}>
          <button onClick={() => abrirFichaPostulante(p)} style={{ display:'flex', alignItems:'center', gap:'0.25rem', padding:'0.375rem 0.7rem', background:'#eff6ff', color:'#1e40af', border:'1px solid #dbeafe', borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.72rem', fontWeight:700, transition:'all 0.15s' }} className="btn-action">
            <Eye size={12} /> Ver
          </button>
          {permisos.gestionarEstadosRRHH && nextEstado && (
            <button onClick={() => cambiarEstadoPostulacion(p.id,nextEstado)} title={`→ ${nextEstado}`} style={{ padding:'0.375rem 0.5rem', background:'#f0fdf4', color:'#16a34a', border:'1px solid #bbf7d0', borderRadius:'0.5rem', cursor:'pointer', display:'flex', alignItems:'center', transition:'all 0.15s' }} className="btn-action">
              <ChevronRight size={13} />
            </button>
          )}
          {puedeValidar && (
            <>
              <button onClick={() => validarPostulacionSupervisor(p.id,true)} style={{ padding:'0.375rem 0.5rem', background:'#f0fdf4', color:'#16a34a', border:'1px solid #bbf7d0', borderRadius:'0.5rem', cursor:'pointer', display:'flex', alignItems:'center' }}><ThumbsUp size={12} /></button>
              <button onClick={() => validarPostulacionSupervisor(p.id,false)} style={{ padding:'0.375rem 0.5rem', background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca', borderRadius:'0.5rem', cursor:'pointer', display:'flex', alignItems:'center' }}><ThumbsDown size={12} /></button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// ── TARJETA ESTADO CANDIDATO (colaborador) ────────────────────────────────────
const TarjetaEstadoCandidato = ({ postulacion:p, onVerDetalle }) => {
  const pasos = [
    { key:'Pendiente Validación Supervisor', label:'Enviada', icon:'' },
    { key:'Enviado', label:'Validada', icon:'' },
    { key:'Recibido en DTHS', label:'Recibida DTHS', icon:'' },
    { key:'Visto en DTHS', label:'Revisada', icon:'' },
    { key:'En Proceso de Revisión', label:'Evaluación', icon:'' },
    { key:'Postulación Aprobada', label:'Resultado', icon:'' },
  ];
  const estadoNorm = normalizarEstado(p.estado);
  const idx = pasos.findIndex(s => s.key === estadoNorm || s.key === p.estado);
  const progreso = idx===-1 ? 0 : Math.round(((idx+1)/pasos.length)*100);
  const rechazada = ['Rechazada por Supervisor','Aprobación Declinada'].includes(p.estado) || estadoNorm === 'Aprobación Declinada';
  const aprobada = p.estado==='Postulación Aprobada' || estadoNorm === 'Postulación Aprobada';
  return (
    <div style={{ border:`1px solid ${aprobada?'#bbf7d0':rechazada?'#fecaca':'#e9eef5'}`, borderRadius:'0.875rem', padding:'1.5rem', backgroundColor: aprobada?'#f0fdf4':rechazada?'#fef2f2':'white', boxShadow:'0 1px 4px rgba(0,0,0,.05)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1rem' }}>
        <div>
          <h3 style={{ fontSize:'1.1rem', fontWeight:700, margin:'0 0 0.25rem', color:'#0f172a' }}>{p.cargoVacante}</h3>
          <p style={{ color:'#64748b', fontSize:'0.8rem', margin:0 }}>Postulación enviada el {p.fechaPostulacion ? new Date(p.fechaPostulacion).toLocaleDateString('es-DO',{ day:'numeric', month:'long', year:'numeric' }) : '—'}</p>
        </div>
        <EstadoBadgeLegacy estado={p.estado} />
      </div>
      {!rechazada ? (
        <>
          <div style={{ marginBottom:'0.75rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.75rem', color:'#94a3b8', marginBottom:'0.375rem' }}>
              <span>Progreso del proceso</span><span style={{ fontWeight:700, color:'#3b82f6' }}>{progreso}%</span>
            </div>
            <div style={{ height:8, backgroundColor:'#f1f5f9', borderRadius:'9999px', overflow:'hidden' }}>
              <div style={{ width:`${progreso}%`, height:'100%', backgroundColor:'#3b82f6', borderRadius:'9999px', transition:'width 0.5s' }} />
            </div>
          </div>
          <div style={{ display:'flex', gap:'0.25rem', overflowX:'auto' }}>
            {pasos.map((paso,i) => (
              <div key={paso.key} style={{ flex:1, textAlign:'center', minWidth:60 }}>
                <div style={{ fontSize:'1rem', marginBottom:'0.25rem', opacity: i<=idx ? 1 : 0.3 }}>{paso.icon}</div>
                <div style={{ fontSize:'0.58rem', color: i<=idx ? '#1e40af' : '#94a3b8', fontWeight: i===idx ? 800 : 400 }}>{paso.label}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ padding:'0.75rem', backgroundColor: rechazada?'#fee2e2':'#f0fdf4', borderRadius:'0.5rem', fontSize:'0.875rem', color: rechazada?'#991b1b':'#166534' }}>
          {p.estado==='Rechazada por Supervisor' ? <><AlertTriangle size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Tu supervisor rechazó esta postulación.</> : <><XCircle size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />La evaluación concluyó. Gracias por participar.</>}
        </div>
      )}
      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'1rem' }}>
        <button onClick={onVerDetalle} style={{ ...btnStyle('#1e40af'), fontSize:'0.8rem', padding:'0.5rem 1rem' }}><Eye size={14} /> Ver detalles</button>
      </div>
    </div>
  );
};

// ============================================================
// MODAL FICHA POSTULANTE — MEJORADA con tab Documentos
// ============================================================
const ModalFichaPostulante = ({ postulacion:p, permisos, cambiarEstadoPostulacion, validarPostulacionSupervisor, user, onClose }) => {
  const [tabActiva, setTabActiva] = useState('perfil');
  const [notaTexto, setNotaTexto] = useState('');
  const [notasLocales, setNotasLocales] = useState([]);
  const [evaluacion, setEvaluacion] = useState({ comunicacion:0, experiencia:0, formacion:0, actitud:0, disponibilidad:0 });
  const [procesando, setProcesando] = useState(false);
  const [docModal, setDocModal] = useState(null);

  const cfg = getEstadoConfig(p.estado);
 const puedeValidar = permisos.validarPostulacionEquipo && (
  p.estado === 'Pendiente Validación Supervisor' ||
  p.estado === 'Pendiente Aprobación Gerente'    ||
  p.estado === 'Pendiente Aprobación Director'
);
  // FIX: usar normalizarEstado para el siguiente estado en la ficha
  const proximoEstado = PROXY_NEXT[normalizarEstado(p.estado)];

  const promedioEval = Object.values(evaluacion).filter(v=>v>0);
  const scorePromedio = promedioEval.length ? (promedioEval.reduce((a,b)=>a+b,0)/promedioEval.length).toFixed(1) : 0;
  const initials = (p.nombreCandidato||p.nombre||'?').split(' ').map(n=>n[0]).slice(0,2).join('').toUpperCase();

  const agregarNota = () => {
    if (!notaTexto.trim()) return;
    setNotasLocales(prev => [...prev, { texto:notaTexto, autor:user.name, fecha:new Date().toISOString(), etapa:p.estado }]);
    setNotaTexto('');
  };

  const tabs = [
    { id:'perfil',     label:'Perfil',      icon:UserCircle },
    { id:'documentos', label:'Documentos',   icon:Paperclip, badge: p.documentos ? Object.keys(p.documentos).length : 0 },
    { id:'evaluacion', label:'Evaluación',   icon:BarChart2, show: permisos.gestionarEstadosRRHH },
    { id:'notas',      label:'Notas',        icon:MessageSquare, badge: notasLocales.length || null },
    { id:'historial',  label:'Historial',    icon:History },
  ].filter(t => t.show!==false);

  const historial = [
    {estado:'Postulación Recibida', fecha:p.fechaPostulacion, actor: p.nombreCandidato || p.nombre || 'Sistema',    icon:Send,        color:'#3b82f6' },
    ...(p.fechaEnvio          ? [{ estado:'Validada por Supervisor',  fecha:p.fechaEnvio,          actor: p.nombreSupervisorValidador || 'Supervisor', icon:UserCheck,   color:'#0891b2' }] : []),
    ...(p.fechaRecibidoRRHH   ? [{ estado:'Recibida en RRHH',         fecha:p.fechaRecibidoRRHH,   actor:'RRHH',       icon:FileCheck,   color:'#7c3aed' }] : []),
    ...(p.fechaVistoRRHH      ? [{ estado:'Vista en RRHH',             fecha:p.fechaVistoRRHH,      actor:'RRHH',       icon:Eye,         color:'#d97706' }] : []),
    ...(p.fechaRevision       ? [{ estado:'En Proceso de Revisión',    fecha:p.fechaRevision,       actor:'RRHH',       icon:Activity,    color:'#059669' }] : []),
  ].filter(h => h.fecha);

 const handleAccion = async (accion) => {
  setProcesando(true);
  try {
    const notasTexto = notasLocales.map(n => `[${new Date(n.fecha).toLocaleString('es-DO')} - ${n.autor}]: ${n.texto}`).join('\n');
    const evalScore  = promedioEval.length ? parseFloat(scorePromedio) : null;

    if      (accion==='avanzar' && proximoEstado) await cambiarEstadoPostulacion(p.id, proximoEstado,  notasTexto, user.empleadoId, evalScore, evaluacion);
    else if (accion==='aprobar')                  await cambiarEstadoPostulacion(p.id, 'Postulación Aprobada',   notasTexto, user.empleadoId, evalScore, evaluacion);
    else if (accion==='declinar')                 await cambiarEstadoPostulacion(p.id, 'Aprobación Declinada',  notasTexto, user.empleadoId, evalScore, evaluacion);
    else if (accion==='validar')                  await validarPostulacionSupervisor(p.id, true);
    else if (accion==='rechazar-supervisor')      await validarPostulacionSupervisor(p.id, false);
  } finally { setProcesando(false); }
};

  const obsData = parseObservaciones(p.motivacion);
  const pipelineOrden = ['Enviado','Recibido en DTHS','Visto en DTHS','En Proceso de Revisión','Postulación Aprobada'];
  const idxActual = pipelineOrden.indexOf(normalizarEstado(p.estado));

  // Config de documentos
  const docConfigs = {
    cv:          { label:'Curriculum Vitae (CV)',  icon:FileText,  color:'#dc2626', bg:'#fef2f2', border:'#fecaca', desc:'Hoja de vida del candidato' },
    fotoCedula:  { label:'Foto de Cédula',          icon:FileBadge, color:'#0891b2', bg:'#ecfeff', border:'#a5f3fc', desc:'Cédula de identidad (ambas caras)' },
    foto2x2:     { label:'Fotografía 2×2',           icon:Image,     color:'#7c3aed', bg:'#f5f3ff', border:'#c4b5fd', desc:'Foto formal reciente' },
  };

  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(15,23,42,0.7)', backdropFilter:'blur(4px)', display:'flex', alignItems:'flex-start', justifyContent:'flex-end', zIndex:1000, padding:0 }}>
      <div style={{ width:'min(820px,100vw)', height:'100vh', backgroundColor:'white', display:'flex', flexDirection:'column', boxShadow:'-12px 0 60px rgba(0,0,0,0.18)', animation:'slideRight 0.28s cubic-bezier(0.16,1,0.3,1)' }}>

        {/* HEADER */}
        <div style={{ background:'linear-gradient(135deg,#0f172a,#1e3a8a)', color:'white', padding:'1.5rem', flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem' }}>
            <div style={{ display:'flex', gap:'1rem', alignItems:'center' }}>
              <div style={{ width:50, height:50, borderRadius:'50%', background:`${cfg.color}30`, border:`2px solid ${cfg.color}60`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', fontWeight:800, color:'white', flexShrink:0 }}>{initials}</div>
              <div>
                <h2 style={{ fontSize:'1.2rem', fontWeight:800, margin:'0 0 0.25rem', letterSpacing:'-0.02em' }}>{p.nombreCandidato||p.nombre}</h2>
                <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap', opacity:0.75 }}>
                  <span style={{ fontSize:'0.78rem', display:'flex', alignItems:'center', gap:'0.25rem' }}><Briefcase size={11} /> {p.cargoVacante}</span>
                  {p.email && <span style={{ fontSize:'0.78rem', display:'flex', alignItems:'center', gap:'0.25rem' }}><Mail size={11} /> {p.email}</span>}
                  {p.telefono && <span style={{ fontSize:'0.78rem', display:'flex', alignItems:'center', gap:'0.25rem' }}><Phone size={11} /> {p.telefono}</span>}
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
              <EstadoBadgeNew cfg={cfg} label={p.estado} />
              <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'0.5rem', padding:'0.5rem', cursor:'pointer', color:'white', display:'flex' }}><X size={16} /></button>
            </div>
          </div>

          {/* Mini pipeline */}
          <div style={{ display:'flex', alignItems:'center' }}>
            {pipelineOrden.map((etapa,i) => {
              const done = idxActual>=i;
              const active = idxActual===i;
              return (
                <React.Fragment key={etapa}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:1 }}>
                    <div style={{ width:24, height:24, borderRadius:'50%', background: done?'white':'rgba(255,255,255,0.15)', border: active?'2px solid rgba(255,255,255,0.7)':'2px solid transparent', display:'flex', alignItems:'center', justifyContent:'center', boxShadow: active?'0 0 0 3px rgba(255,255,255,0.2)':'none', transition:'all 0.3s' }}>
                      {done ? <CheckCircle size={13} style={{ color:'#1e40af' }} /> : <span style={{ fontSize:'0.58rem', fontWeight:800, color:'rgba(255,255,255,0.4)' }}>{i+1}</span>}
                    </div>
                    <span style={{ fontSize:'0.52rem', opacity: done?0.9:0.4, marginTop:'0.2rem', textAlign:'center', lineHeight:1.2, fontWeight:600 }}>{etapa.replace(' en RRHH','').replace('Postulación ','')}</span>
                  </div>
                  {i<pipelineOrden.length-1 && <div style={{ flex:'0 0 18px', height:2, background: idxActual>i?'rgba(255,255,255,0.5)':'rgba(255,255,255,0.15)', marginBottom:14, borderRadius:2 }} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* TABS */}
        <div style={{ display:'flex', borderBottom:'1px solid #f1f5f9', backgroundColor:'#fafbfc', flexShrink:0, overflowX:'auto' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setTabActiva(tab.id)} style={{ padding:'0.75rem 1.1rem', border:'none', backgroundColor:'transparent', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, color: tabActiva===tab.id ? '#1e40af' : '#6b7280', borderBottom:`2px solid ${tabActiva===tab.id ? '#1e40af' : 'transparent'}`, display:'flex', alignItems:'center', gap:'0.35rem', whiteSpace:'nowrap', transition:'all 0.15s' }}>
              <tab.icon size={13} /> {tab.label}
              {tab.badge ? <span style={{ fontSize:'0.62rem', background: tabActiva===tab.id?'#1e40af':'#e2e8f0', color: tabActiva===tab.id?'white':'#64748b', borderRadius:'9999px', padding:'0.1rem 0.4rem', fontWeight:800 }}>{tab.badge}</span> : null}
            </button>
          ))}
        </div>

        {/* CONTENIDO TABS */}
        <div style={{ flex:1, overflowY:'auto', padding:'1.5rem' }}>

          {/* ── PERFIL */}
          {tabActiva==='perfil' && (
            <div style={{ display:'grid', gap:'1rem', animation:'fadeUp 0.2s ease-out' }}>
              <SeccionFicha titulo="Información de Contacto" icono={User}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                  <DatoFicha label="Nombre completo" valor={p.nombreCandidato||p.nombre} />
                  <DatoFicha label="Cédula" valor={p.cedula} />
                  <DatoFicha label="Email" valor={p.email} />
                  <DatoFicha label="Teléfono" valor={p.telefono} />
                  <DatoFicha label="Tipo de postulante" valor={<TipoBadge tipo={p.tipoPostulante} />} />
                  <DatoFicha label="Fecha de postulación" valor={p.fechaPostulacion ? new Date(p.fechaPostulacion).toLocaleDateString('es-DO',{ day:'numeric', month:'long', year:'numeric' }) : '—'} />
                  <DatoFicha label="Idiomas" valor={p.idiomas} />
                  <DatoFicha label="Cómo se enteró" valor={p.comoConocioVacante} />
                </div>
              </SeccionFicha>

              <SeccionFicha titulo="Formación Académica" icono={GraduationCap}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                  <DatoFicha label="Nivel educativo" valor={p.nivelEducativo} />
                  <DatoFicha label="Institución" valor={p.institucion} />
                  <DatoFicha label="Título obtenido" valor={p.tituloObtenido} />
                  <DatoFicha label="Año de graduación" valor={p.anioGraduacion} />
                </div>
              </SeccionFicha>

              <SeccionFicha titulo="Experiencia Laboral" icono={Briefcase}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'0.625rem' }}>
                  <DatoFicha label="Años de experiencia" valor={p.experienciaAnios ? `${p.experienciaAnios} años` : null} />
                  <DatoFicha label="Último empleador" valor={p.ultimoEmpleador} />
                  <DatoFicha label="Último puesto" valor={p.ultimoPuesto} />
                  <DatoFicha label="Expectativa salarial" valor={p.expectativaSalarial ? `RD$${Number(p.expectativaSalarial).toLocaleString()}` : null} />
                </div>
              </SeccionFicha>

              {obsData['Motivación'] && <SeccionFicha titulo="Motivación para Aplicar" icono={Target}><p style={{ fontSize:'0.875rem', color:'#374151', lineHeight:1.75, margin:0, whiteSpace:'pre-wrap' }}>{obsData['Motivación']}</p></SeccionFicha>}
              {obsData['Logros actuales'] && <SeccionFicha titulo="Logros en Puesto Actual" icono={Award}><p style={{ fontSize:'0.875rem', color:'#374151', lineHeight:1.75, margin:0, whiteSpace:'pre-wrap' }}>{obsData['Logros actuales']}</p></SeccionFicha>}
              {obsData['Competencias'] && <SeccionFicha titulo="Competencias Declaradas" icono={Layers}><p style={{ fontSize:'0.875rem', color:'#374151', lineHeight:1.75, margin:0, whiteSpace:'pre-wrap' }}>{obsData['Competencias']}</p></SeccionFicha>}

              {(obsData['Años en puesto actual']||obsData['Disponibilidad']||obsData['Expectativa salarial']||p.referenciaPersonal1) && (
                <SeccionFicha titulo="Disponibilidad & Referencias" icono={ClipboardList}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                    {obsData['Años en puesto actual'] && <DatoFicha label="Años en puesto actual" valor={obsData['Años en puesto actual']} />}
                    {obsData['Disponibilidad'] && <DatoFicha label="Disponibilidad" valor={obsData['Disponibilidad']} />}
                    {p.referenciaPersonal1 && <DatoFicha label="Referencia 1" valor={p.referenciaPersonal1} />}
                    {p.referenciaPersonal2 && <DatoFicha label="Referencia 2" valor={p.referenciaPersonal2} />}
                  </div>
                </SeccionFicha>
              )}

              {!obsData['Motivación'] && p.motivacion && (
                <SeccionFicha titulo="Información de la Postulación" icono={FileText}><p style={{ fontSize:'0.875rem', color:'#374151', lineHeight:1.75, margin:0, whiteSpace:'pre-wrap' }}>{p.motivacion}</p></SeccionFicha>
              )}
            </div>
          )}

          {/* ── DOCUMENTOS */}
          {tabActiva==='documentos' && (
            <div style={{ animation:'fadeUp 0.2s ease-out' }}>
              <div style={{ marginBottom:'1rem', padding:'0.75rem 1rem', background:'#eff6ff', border:'1px solid #dbeafe', borderRadius:'0.75rem', display:'flex', alignItems:'center', gap:'0.75rem' }}>
                <Paperclip size={15} style={{ color:'#1e40af' }} />
                <p style={{ fontSize:'0.8rem', color:'#1e40af', margin:0, fontWeight:500 }}>Documentos adjuntados por el candidato al momento de postularse. Máximo 5MB por archivo.</p>
              </div>

              <div style={{ display:'grid', gap:'0.875rem' }}>
                {p.documentos && Object.entries(p.documentos).length > 0 ? (
                  Object.entries(p.documentos).map(([key, doc]) => {
                    const dcfg = docConfigs[key] || { label:key, icon:FileText, color:'#64748b', bg:'#f8fafc', border:'#e2e8f0', desc:'' };
                    const isPdf = doc.tipo==='pdf' || doc.nombre?.toLowerCase().endsWith('.pdf');
                    const isImg = ['jpg','jpeg','png','gif','webp'].some(ext => doc.nombre?.toLowerCase().endsWith(`.${ext}`));
                    return (
                      <div key={key} style={{ border:`1px solid ${dcfg.border}`, borderRadius:'0.875rem', overflow:'hidden', background:'white', boxShadow:'0 1px 3px rgba(0,0,0,.04)' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'1rem 1.25rem', background:dcfg.bg }}>
                          <div style={{ width:44, height:44, borderRadius:'0.625rem', background:`${dcfg.color}15`, border:`1px solid ${dcfg.color}30`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            <dcfg.icon size={20} style={{ color:dcfg.color }} />
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:'0.875rem', fontWeight:700, color:'#0f172a', marginBottom:'0.15rem' }}>{dcfg.label}</div>
                            <div style={{ fontSize:'0.72rem', color:'#64748b' }}>{dcfg.desc}</div>
                          </div>
                          <div style={{ display:'flex', gap:'0.5rem' }}>
                            <button onClick={() => setDocModal({ ...doc, label:dcfg.label, isPdf, isImg, dcfg })} className="btn-action" style={{ display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.45rem 0.875rem', background:dcfg.color, color:'white', border:'none', borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.75rem', fontWeight:700, transition:'all 0.15s' }}>
                              <Eye size={13} /> Ver
                            </button>
                            <a href={doc.url||'#'} download={doc.nombre} style={{ display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.45rem 0.875rem', background:'white', color:dcfg.color, border:`1px solid ${dcfg.border}`, borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.75rem', fontWeight:700, textDecoration:'none', transition:'all 0.15s' }}>
                              <Download size={13} /> Descargar
                            </a>
                          </div>
                        </div>
                        <div style={{ padding:'0.5rem 1.25rem', display:'flex', alignItems:'center', gap:'0.5rem', borderTop:`1px solid ${dcfg.border}` }}>
                          <Hash size={11} style={{ color:'#94a3b8' }} />
                          <span style={{ fontSize:'0.72rem', color:'#64748b', fontFamily:'monospace' }}>{doc.nombre||'Sin nombre'}</span>
                          <span style={{ marginLeft:'auto', fontSize:'0.65rem', padding:'0.12rem 0.45rem', background:'#f1f5f9', borderRadius:'9999px', color:'#64748b', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                            {isPdf ? 'PDF' : isImg ? 'IMAGEN' : 'ARCHIVO'}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign:'center', padding:'3rem', background:'#fafbfc', borderRadius:'0.875rem', border:'1px dashed #e2e8f0' }}>
                    <Paperclip size={40} style={{ color:'#cbd5e1', margin:'0 auto 0.75rem' }} />
                    <p style={{ color:'#94a3b8', fontSize:'0.875rem', margin:0 }}>No hay documentos adjuntos para este candidato</p>
                  </div>
                )}
              </div>

              <div style={{ marginTop:'1rem', padding:'0.875rem 1rem', background:'#f8fafc', border:'1px solid #e9eef5', borderRadius:'0.75rem' }}>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.625rem' }}>Documentos requeridos</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.5rem' }}>
                  {Object.entries(docConfigs).map(([key,dcfg]) => {
                    const tiene = p.documentos && p.documentos[key];
                    return (
                      <div key={key} style={{ display:'flex', alignItems:'center', gap:'0.375rem', fontSize:'0.75rem' }}>
                        {tiene ? <CheckCircle size={13} style={{ color:'#16a34a', flexShrink:0 }} /> : <XCircle size={13} style={{ color:'#dc2626', flexShrink:0 }} />}
                        <span style={{ color: tiene?'#166534':'#991b1b', fontWeight:600 }}>{dcfg.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── EVALUACION */}
          {tabActiva==='evaluacion' && permisos.gestionarEstadosRRHH && (
            <div style={{ display:'grid', gap:'1rem', animation:'fadeUp 0.2s ease-out' }}>
              <SeccionFicha titulo="Evaluación por Competencias" icono={BarChart2}>
                <p style={{ fontSize:'0.78rem', color:'#64748b', margin:'0 0 1.25rem' }}>Califica cada competencia del 1 al 10 con base en la información del candidato.</p>
                <div style={{ display:'grid', gap:'1.125rem' }}>
                  {[
                    { key:'comunicacion',   label:'Comunicación',        desc:'Claridad y expresión escrita' },
                    { key:'experiencia',    label:'Experiencia Relevante', desc:'Alineación con los requisitos' },
                    { key:'formacion',      label:'Formación Académica',   desc:'Nivel educativo y certificaciones' },
                    { key:'actitud',        label:'Actitud y Motivación',  desc:'Entusiasmo expresado' },
                    { key:'disponibilidad', label:'Disponibilidad',        desc:'Disponibilidad para el rol' },
                  ].map(comp => {
                    const val = evaluacion[comp.key];
                    const scoreColor = val>=7 ? '#16a34a' : val>=5 ? '#d97706' : val>0 ? '#dc2626' : '#cbd5e1';
                    return (
                      <div key={comp.key}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
                          <div>
                            <span style={{ fontSize:'0.83rem', fontWeight:700, color:'#0f172a' }}>{comp.label}</span>
                            <span style={{ fontSize:'0.72rem', color:'#94a3b8', marginLeft:'0.5rem' }}>{comp.desc}</span>
                          </div>
                          <span style={{ fontSize:'1rem', fontWeight:800, color:scoreColor, minWidth:40, textAlign:'right' }}>{val>0 ? `${val}/10` : '—'}</span>
                        </div>
                        <div style={{ display:'flex', gap:'0.25rem' }}>
                          {[1,2,3,4,5,6,7,8,9,10].map(n => {
                            const active = val>=n;
                            const btnCol = n>=7?'#16a34a':n>=5?'#d97706':'#dc2626';
                            return (
                              <button key={n} onClick={() => setEvaluacion(prev => ({ ...prev, [comp.key]:n }))} style={{ flex:1, height:28, border:'none', borderRadius:'0.25rem', cursor:'pointer', fontSize:'0.68rem', fontWeight:800, background: active?btnCol:'#f1f5f9', color: active?'white':'#94a3b8', transition:'all 0.12s' }}>{n}</button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SeccionFicha>

              {promedioEval.length>0 && (
                <div style={{ background:'linear-gradient(135deg,#f8fafc,white)', border:'1px solid #e2e8f0', borderRadius:'0.875rem', padding:'1.25rem', display:'flex', alignItems:'center', gap:'1.5rem' }}>
                  <div style={{ textAlign:'center', flexShrink:0 }}>
                    <div style={{ fontSize:'3rem', fontWeight:900, color: parseFloat(scorePromedio)>=7?'#16a34a':parseFloat(scorePromedio)>=5?'#d97706':'#dc2626', lineHeight:1, letterSpacing:'-0.05em' }}>{scorePromedio}</div>
                    <div style={{ fontSize:'0.65rem', color:'#94a3b8', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginTop:4 }}>Puntaje</div>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ height:10, background:'#f1f5f9', borderRadius:'9999px', overflow:'hidden', marginBottom:'0.625rem' }}>
                      <div style={{ width:`${parseFloat(scorePromedio)*10}%`, height:'100%', background: parseFloat(scorePromedio)>=7?'#16a34a':parseFloat(scorePromedio)>=5?'#d97706':'#dc2626', borderRadius:'9999px', transition:'width 0.5s' }} />
                    </div>
                    <p style={{ fontSize:'0.82rem', color:'#64748b', margin:0, fontWeight:500 }}>
                      {parseFloat(scorePromedio)>=7 ? <><CheckCircle size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Perfil recomendado para continuar</> : parseFloat(scorePromedio)>=5 ? <><AlertTriangle size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Perfil con potencial, revisar detalles</> : <><XCircle size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Perfil no cumple los criterios mínimos</>}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── NOTAS */}
          {tabActiva==='notas' && (
            <div style={{ display:'grid', gap:'1rem', animation:'fadeUp 0.2s ease-out' }}>
              <SeccionFicha titulo="Agregar Nota" icono={MessageSquare}>
                <textarea value={notaTexto} onChange={e => setNotaTexto(e.target.value)} placeholder="Observaciones de entrevista, comentarios del equipo, puntos importantes..." rows={4} style={{ width:'100%', padding:'0.75rem', border:'1px solid #e2e8f0', borderRadius:'0.625rem', fontSize:'0.875rem', resize:'vertical', outline:'none', boxSizing:'border-box', fontFamily:'inherit', transition:'all 0.2s' }} />
                <button onClick={agregarNota} disabled={!notaTexto.trim()} style={{ marginTop:'0.75rem', padding:'0.55rem 1.25rem', backgroundColor: notaTexto.trim()?'#1e40af':'#e2e8f0', color: notaTexto.trim()?'white':'#94a3b8', border:'none', borderRadius:'0.5rem', cursor: notaTexto.trim()?'pointer':'not-allowed', fontSize:'0.8rem', fontWeight:700, transition:'all 0.15s' }}>
                  Guardar nota
                </button>
              </SeccionFicha>

              <SeccionFicha titulo={`Notas de sesión (${notasLocales.length})`} icono={ClipboardList}>
                {notasLocales.length===0 ? (
                  <div style={{ textAlign:'center', padding:'2rem', color:'#cbd5e1' }}>
                    <MessageSquare size={32} style={{ margin:'0 auto 0.5rem' }} />
                    <p style={{ fontSize:'0.82rem', margin:0 }}>Sin notas aún. Agrega la primera arriba.</p>
                  </div>
                ) : (
                  <div style={{ display:'grid', gap:'0.75rem' }}>
                    {notasLocales.slice().reverse().map((nota,i) => (
                      <div key={i} style={{ padding:'0.875rem 1rem', backgroundColor:'#fffbeb', borderRadius:'0.625rem', border:'1px solid #fde68a' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.5rem' }}>
                          <span style={{ fontSize:'0.75rem', fontWeight:800, color:'#92400e' }}>{nota.autor}</span>
                          <span style={{ fontSize:'0.68rem', color:'#a16207' }}>{new Date(nota.fecha).toLocaleString('es-DO')}</span>
                        </div>
                        <p style={{ fontSize:'0.875rem', color:'#374151', margin:'0 0 0.375rem', lineHeight:1.6 }}>{nota.texto}</p>
                        <span style={{ fontSize:'0.65rem', padding:'0.12rem 0.45rem', backgroundColor:'#fef3c7', borderRadius:'9999px', color:'#92400e', fontWeight:700 }}>Etapa: {nota.etapa}</span>
                      </div>
                    ))}
                  </div>
                )}
              </SeccionFicha>
            </div>
          )}

          {/* ── HISTORIAL */}
          {tabActiva==='historial' && (
            <div style={{ animation:'fadeUp 0.2s ease-out' }}>
              <SeccionFicha titulo="Línea de Tiempo del Proceso" icono={History}>
                {historial.length===0 ? (
                  <p style={{ fontSize:'0.875rem', color:'#94a3b8', textAlign:'center', padding:'1.5rem' }}>Sin historial disponible</p>
                ) : (
                  <div style={{ position:'relative', paddingLeft:'1rem' }}>
                    <div style={{ position:'absolute', left:15, top:16, bottom:0, width:2, background:'linear-gradient(to bottom,#3b82f6,#f1f5f9)' }} />
                    {historial.map((h,i) => (
                      <div key={i} style={{ display:'flex', gap:'1rem', paddingBottom:'1.25rem', position:'relative' }}>
                        <div style={{ width:30, height:30, borderRadius:'50%', backgroundColor:h.color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, position:'relative', zIndex:1, boxShadow:`0 0 0 4px ${h.color}20` }}>
                          <h.icon size={13} style={{ color:'white' }} />
                        </div>
                        <div style={{ paddingTop:'0.25rem' }}>
                          <div style={{ fontSize:'0.875rem', fontWeight:700, color:'#0f172a' }}>{h.estado}</div>
                          <div style={{ fontSize:'0.72rem', color:'#64748b', marginTop:'0.15rem' }}>
                            {h.actor} · {new Date(h.fecha).toLocaleString('es-DO',{ day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SeccionFicha>
            </div>
          )}
        </div>

        {/* FOOTER ACCIONES */}
        <div style={{ padding:'1rem 1.5rem', borderTop:'2px solid #f1f5f9', backgroundColor:'#fafbfc', flexShrink:0 }}>
          <div style={{ display:'flex', gap:'0.625rem', flexWrap:'wrap', alignItems:'center' }}>
            {puedeValidar && (
              <>
                <button onClick={() => handleAccion('validar')} disabled={procesando} className="btn-action" style={{ ...btnStyle('#16a34a'), padding:'0.65rem 1.1rem' }}><ThumbsUp size={14} /> Validar y Enviar a RRHH</button>
                <button onClick={() => handleAccion('rechazar-supervisor')} disabled={procesando} className="btn-action" style={{ ...btnStyle('#dc2626',true), padding:'0.65rem 1.1rem' }}><ThumbsDown size={14} /> Rechazar</button>
              </>
            )}
            {permisos.gestionarEstadosRRHH && proximoEstado && (
              <button onClick={() => handleAccion('avanzar')} disabled={procesando} className="btn-action" style={{ ...btnStyle('#1e40af'), padding:'0.65rem 1.1rem' }}>
                {procesando ? <Loader size={14} style={{ animation:'spin 0.8s linear infinite' }} /> : <ChevronRight size={14} />}
                Avanzar: <strong style={{ marginLeft:2 }}>{getEstadoConfig(proximoEstado).label}</strong>
              </button>
            )}
            {permisos.gestionarEstadosRRHH && normalizarEstado(p.estado)==='En Proceso de Revisión' && (
              <>
                <button onClick={() => handleAccion('aprobar')} disabled={procesando} className="btn-action" style={{ ...btnStyle('#16a34a'), padding:'0.65rem 1.1rem' }}><CheckCircle size={14} /> Aprobar Candidato</button>
                <button onClick={() => handleAccion('declinar')} disabled={procesando} className="btn-action" style={{ ...btnStyle('#dc2626',true), padding:'0.65rem 1.1rem' }}><XCircle size={14} /> Declinar Candidato</button>
              </>
            )}
            <button onClick={onClose} style={{ marginLeft:'auto', padding:'0.65rem 1.1rem', backgroundColor:'white', color:'#6b7280', border:'1px solid #e2e8f0', borderRadius:'0.625rem', cursor:'pointer', fontSize:'0.82rem', fontWeight:700 }}>Cerrar</button>
          </div>
        </div>
      </div>

      {/* DOCUMENT VIEWER MODAL */}
      {docModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }} onClick={() => setDocModal(null)}>
          <div style={{ background:'white', borderRadius:'1.25rem', maxWidth:680, width:'100%', overflow:'hidden', boxShadow:'0 30px 80px rgba(0,0,0,0.4)', animation:'fadeUp 0.25s ease-out' }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.5rem', borderBottom:'1px solid #f1f5f9', background:docModal.dcfg?.bg }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                <docModal.dcfg.icon size={18} style={{ color:docModal.dcfg?.color }} />
                <div>
                  <div style={{ fontSize:'0.9rem', fontWeight:700, color:'#0f172a' }}>{docModal.label}</div>
                  <div style={{ fontSize:'0.72rem', color:'#64748b', fontFamily:'monospace' }}>{docModal.nombre}</div>
                </div>
              </div>
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <a href={docModal.url||'#'} download={docModal.nombre} style={{ display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.5rem 0.875rem', background:docModal.dcfg?.color, color:'white', border:'none', borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, textDecoration:'none' }}><Download size={13} /> Descargar</a>
                <button onClick={() => setDocModal(null)} style={{ padding:'0.5rem', background:'white', border:'1px solid #e2e8f0', borderRadius:'0.5rem', cursor:'pointer', display:'flex', color:'#64748b' }}><X size={16} /></button>
              </div>
            </div>
            <div style={{ padding:'2.5rem', background:'#f8fafc', minHeight:280, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {docModal.isPdf ? (
                <div style={{ textAlign:'center' }}>
                  <FileText size={64} style={{ color:'#dc2626', margin:'0 auto 1rem' }} />
                  <p style={{ fontSize:'0.875rem', color:'#64748b', margin:'0 0 0.375rem' }}>Documento PDF</p>
                  <p style={{ fontSize:'0.78rem', color:'#94a3b8', fontFamily:'monospace', margin:'0 0 1.5rem' }}>{docModal.nombre}</p>
                  <a href={docModal.url||'#'} target="_blank" rel="noopener noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.625rem 1.25rem', background:'#dc2626', color:'white', border:'none', borderRadius:'0.625rem', cursor:'pointer', fontSize:'0.82rem', fontWeight:700, textDecoration:'none' }}>
                    <ExternalLink size={14} /> Abrir en nueva pestaña
                  </a>
                </div>
              ) : (
                <div style={{ textAlign:'center' }}>
                  {docModal.url && docModal.url!=='#' ? (
                    <img src={docModal.url} alt={docModal.label} style={{ maxWidth:'100%', maxHeight:400, borderRadius:'0.75rem', boxShadow:'0 8px 24px rgba(0,0,0,.12)' }} />
                  ) : (
                    <>
                      <div style={{ width:180, height:180, borderRadius:'1rem', background:`${docModal.dcfg?.color}12`, border:`2px dashed ${docModal.dcfg?.color}40`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1rem' }}>
                        <Image size={48} style={{ color:docModal.dcfg?.color, opacity:0.5 }} />
                      </div>
                      <p style={{ fontSize:'0.875rem', color:'#64748b', margin:'0 0 0.375rem' }}>Vista previa no disponible</p>
                      <p style={{ fontSize:'0.72rem', color:'#94a3b8', fontFamily:'monospace', margin:'0 0 1.5rem' }}>{docModal.nombre}</p>
                      <a href={docModal.url||'#'} download={docModal.nombre} style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.625rem 1.25rem', background:docModal.dcfg?.color, color:'white', border:'none', borderRadius:'0.625rem', cursor:'pointer', fontSize:'0.82rem', fontWeight:700, textDecoration:'none' }}>
                        <Download size={14} /> Descargar imagen
                      </a>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideRight { from { transform:translateX(120%); opacity:0; } to { transform:translateX(0); opacity:1; } } @keyframes fadeUp { from { transform:translateY(8px); opacity:0; } to { transform:translateY(0); opacity:1; } } @keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
};

// ============================================================
// MODAL POSTULACION INTERNA
// ============================================================
const ModalPostulacionInterna = ({ vacante, user, onClose, onGuardar }) => {
  const [datos, setDatos] = useState({ nombre:user?.name||'', email:user?.email||'', telefono:user?.telefono||user?.phone||'', aniosExperienciaActual:'', logrosActuales:'', motivacion:'', competencias:'', disponibilidadCambio:'Inmediata', expectativaSalarial:'' });
  const [errores, setErrores] = useState({});

  const validar = () => {
    const e = {};
    if (!datos.nombre.trim()) e.nombre='Requerido';
    if (!datos.email.trim()) e.email='Requerido';
    if (!datos.motivacion.trim()) e.motivacion='Por favor explica tu motivación';
    if (!datos.logrosActuales.trim()) e.logrosActuales='Por favor describe tus logros';
    if (!datos.competencias.trim()) e.competencias='Por favor describe tus competencias';
    setErrores(e);
    return Object.keys(e).length===0;
  };

  const handleSubmit = (e) => { e.preventDefault(); if (validar()) onGuardar(datos); };
  const err = (campo) => errores[campo] && <div style={{ color:'#dc2626', fontSize:'0.7rem', marginTop:'0.25rem', display:'flex', alignItems:'center', gap:'0.25rem' }}><AlertCircle size={11} />{errores[campo]}</div>;
  const isAutoFilled = (campo) => { const auto={ nombre:user?.name, email:user?.email, telefono:user?.telefono||user?.phone }; return auto[campo] && datos[campo]===auto[campo]; };

  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(15,23,42,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'1rem' }}>
      <div style={{ backgroundColor:'white', borderRadius:'1rem', maxWidth:'700px', width:'100%', maxHeight:'92vh', overflow:'auto', boxShadow:'0 25px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ background:'linear-gradient(135deg,#0f172a,#1e40af)', color:'white', padding:'1.5rem 2rem', borderRadius:'1rem 1rem 0 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <h3 style={{ fontSize:'1.25rem', fontWeight:800, margin:'0 0 0.375rem', letterSpacing:'-0.01em' }}>Postulación Interna</h3>
              <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', opacity:0.85 }}><Briefcase size={14} /><span style={{ fontSize:'0.875rem' }}>{vacante?.titulo} — {vacante?.departamento}</span></div>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:'0.5rem', padding:'0.5rem', cursor:'pointer', color:'white', display:'flex' }}><X size={18} /></button>
          </div>
          <div style={{ marginTop:'1rem', padding:'0.75rem', backgroundColor:'rgba(255,255,255,0.1)', borderRadius:'0.625rem', display:'flex', alignItems:'center', gap:'0.75rem', border:'1px solid rgba(255,255,255,0.15)' }}>
            <Zap size={16} style={{ color:'#fbbf24', flexShrink:0 }} />
            <span style={{ fontSize:'0.8rem' }}>Tus datos de contacto han sido pre-completados desde tu perfil.</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding:'1.5rem 2rem', display:'grid', gap:'1rem' }}>
          <div style={{ backgroundColor:'#f8fafc', borderRadius:'0.75rem', padding:'1rem', border:'1px solid #e2e8f0' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.875rem' }}>
              <User size={14} style={{ color:'#1e40af' }} />
              <span style={{ fontSize:'0.75rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>Datos del Colaborador</span>
              <span style={{ fontSize:'0.65rem', padding:'0.1rem 0.5rem', backgroundColor:'#dbeafe', color:'#1e40af', borderRadius:'9999px', fontWeight:700 }}>Auto-completado</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
              <div><label style={labelSt}>Nombre Completo</label><input value={datos.nombre} onChange={e => setDatos(p => ({ ...p, nombre:e.target.value }))} style={{ ...inputSt, backgroundColor: isAutoFilled('nombre')?'#eff6ff':'white', borderColor: isAutoFilled('nombre')?'#bfdbfe':'#e2e8f0' }} />{err('nombre')}</div>
              <div><label style={labelSt}>Correo Electrónico</label><input type="email" value={datos.email} onChange={e => setDatos(p => ({ ...p, email:e.target.value }))} style={{ ...inputSt, backgroundColor: isAutoFilled('email')?'#eff6ff':'white', borderColor: isAutoFilled('email')?'#bfdbfe':'#e2e8f0' }} />{err('email')}</div>
              <div><label style={labelSt}>Teléfono</label><input value={datos.telefono} onChange={e => setDatos(p => ({ ...p, telefono:e.target.value }))} placeholder="(809) 000-0000" style={{ ...inputSt, backgroundColor: isAutoFilled('telefono')?'#eff6ff':'white' }} /></div>
              <div><label style={labelSt}>Años en Puesto Actual</label><input type="number" value={datos.aniosExperienciaActual} onChange={e => setDatos(p => ({ ...p, aniosExperienciaActual:e.target.value }))} placeholder="ej. 2" min="0" style={inputSt} /></div>
            </div>
          </div>

          <div><label style={labelSt}>Logros en tu Puesto Actual <span style={{ color:'#dc2626' }}>*</span></label><textarea value={datos.logrosActuales} onChange={e => setDatos(p => ({ ...p, logrosActuales:e.target.value }))} rows={3} placeholder="Describe tus principales logros y contribuciones..." style={{ ...inputSt, resize:'vertical' }} required />{err('logrosActuales')}</div>
          <div><label style={labelSt}>Competencias Relevantes para esta Vacante <span style={{ color:'#dc2626' }}>*</span></label><textarea value={datos.competencias} onChange={e => setDatos(p => ({ ...p, competencias:e.target.value }))} rows={3} placeholder="¿Qué habilidades te hacen el candidato ideal?" style={{ ...inputSt, resize:'vertical' }} required />{err('competencias')}</div>
          <div><label style={labelSt}>¿Por qué te interesa esta vacante? <span style={{ color:'#dc2626' }}>*</span></label><textarea value={datos.motivacion} onChange={e => setDatos(p => ({ ...p, motivacion:e.target.value }))} rows={4} placeholder="Explica tu motivación y cómo se alinea con tu desarrollo..." style={{ ...inputSt, resize:'vertical' }} required />{err('motivacion')}</div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
            <div><label style={labelSt}>Disponibilidad para el Cambio</label><select value={datos.disponibilidadCambio} onChange={e => setDatos(p => ({ ...p, disponibilidadCambio:e.target.value }))} style={inputSt}><option value="Inmediata">Inmediata</option><option value="2 semanas">2 semanas</option><option value="1 mes">1 mes</option><option value="A coordinar">A coordinar</option></select></div>
            <div><label style={labelSt}>Expectativa Salarial (RD$, Opcional)</label><input type="text" inputMode="numeric" value={datos.expectativaSalarial ? Number(String(datos.expectativaSalarial).replace(/\D/g,'')).toLocaleString('es-DO') : ''} onChange={e => setDatos(p => ({ ...p, expectativaSalarial:e.target.value.replace(/\D/g,'') }))} placeholder="Ej. 45,000" style={inputSt} /></div>
          </div>

          <div style={{ padding:'0.875rem', backgroundColor:'#f0f9ff', borderRadius:'0.625rem', border:'1px solid #bae6fd', display:'flex', gap:'0.75rem', alignItems:'flex-start' }}>
            <ArrowRight size={15} style={{ color:'#0369a1', flexShrink:0, marginTop:2 }} />
            <div style={{ fontSize:'0.775rem', color:'#0369a1', lineHeight:1.6 }}><strong>Flujo:</strong> Al enviar → Tu supervisor validará → RRHH recibe → Revisión → Resultado final.</div>
          </div>

          <div style={{ display:'flex', gap:'0.75rem', paddingTop:'0.5rem' }}>
            <button type="submit" style={{ flex:1, padding:'0.875rem', backgroundColor:'#1e40af', color:'white', border:'none', borderRadius:'0.625rem', cursor:'pointer', fontSize:'0.875rem', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem' }}><Send size={15} /> Enviar Postulación</button>
            <button type="button" onClick={onClose} style={{ padding:'0.875rem 1.25rem', backgroundColor:'white', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'0.625rem', cursor:'pointer', fontSize:'0.875rem', fontWeight:600 }}>Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// MODALES BASE
// ============================================================
const ModalBase = ({ titulo, children, onClose, ancho='560px' }) => (
  <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(15,23,42,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'1rem' }}>
    <div style={{ backgroundColor:'white', borderRadius:'1rem', maxWidth:ancho, width:'100%', maxHeight:'92vh', overflow:'auto', boxShadow:'0 25px 60px rgba(0,0,0,0.2)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1.25rem 1.5rem', borderBottom:'1px solid #f1f5f9', background:'#fafbfc' }}>
        <h3 style={{ fontSize:'1.1rem', fontWeight:800, margin:0, color:'#0f172a' }}>{titulo}</h3>
        <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#6b7280', padding:'0.25rem', borderRadius:'0.375rem', display:'flex' }}><X size={20} /></button>
      </div>
      <div style={{ padding:'1.5rem' }}>{children}</div>
    </div>
  </div>
);

const ModalSolicitud = ({ onClose, onGuardar, departamentos }) => {
  const [d, setD] = useState({ titulo:'', cargo:'', departamentoId:'', descripcion:'', requisitos:'', justificacion:'', salarioMinimo:'', salarioMaximo:'', modalidad:'Presencial', fechaCierreEstimada:'', numeroVacantes:'1', urgencia:'Media' });
  const upd = (k,v) => setD(p => ({ ...p, [k]:v }));
  const handleSubmit = (e) => { e.preventDefault(); if (!d.departamentoId) return; onGuardar({ Titulo:d.titulo, Descripcion:d.descripcion, Requisitos:d.requisitos, SalarioMinimo:parseFloat(d.salarioMinimo), SalarioMaximo:parseFloat(d.salarioMaximo), DepartamentoID:parseInt(d.departamentoId), cargo:d.cargo, modalidad:d.modalidad, justificacion:d.justificacion, fechaCierreEstimada:d.fechaCierreEstimada||null, numeroVacantes:parseInt(d.numeroVacantes), urgencia:d.urgencia, estado:'Pendiente', fechaSolicitud:new Date().toISOString() }); };
  return (
    <ModalBase titulo="Nueva Solicitud de Vacante" onClose={onClose} ancho="720px">
      <form onSubmit={handleSubmit} style={{ display:'grid', gap:'0.875rem' }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'0.75rem' }}>
          <div><label style={labelSt}>Título de la Vacante *</label><input value={d.titulo} onChange={e => upd('titulo',e.target.value)} style={inputSt} required placeholder="ej. Analista de Sistemas" /></div>
          <div><label style={labelSt}>Cargo *</label><input value={d.cargo} onChange={e => upd('cargo',e.target.value)} style={inputSt} required placeholder="ej. Analista" /></div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:'0.75rem' }}>
          <div><label style={labelSt}>Departamento *</label><SearchableSelect value={d.departamentoId} onChange={v => upd('departamentoId', v)} options={departamentos.map(dep => ({ id: dep.id, label: dep.nombre }))} placeholder="Buscar departamento..." emptyLabel="Seleccionar" inputStyle={inputSt} /></div>
          <div><label style={labelSt}>N° Vacantes</label><input type="number" value={d.numeroVacantes} onChange={e => upd('numeroVacantes',e.target.value)} min="1" style={inputSt} /></div>
          <div><label style={labelSt}>Prioridad</label><select value={d.urgencia} onChange={e => upd('urgencia',e.target.value)} style={inputSt}><option value="Baja">Baja</option><option value="Media">Media</option><option value="Alta">Alta</option><option value="Urgente">Urgente</option></select></div>
        </div>
        <div><label style={labelSt}>Descripción *</label><textarea value={d.descripcion} onChange={e => upd('descripcion',e.target.value)} rows={3} style={{ ...inputSt, resize:'vertical' }} required /></div>
        <div><label style={labelSt}>Requisitos *</label><textarea value={d.requisitos} onChange={e => upd('requisitos',e.target.value)} rows={3} style={{ ...inputSt, resize:'vertical' }} required /></div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.75rem' }}>
          <div><label style={labelSt}>Salario Mín (RD$) *</label><input type="number" value={d.salarioMinimo} onChange={e => upd('salarioMinimo',e.target.value)} style={inputSt} required /></div>
          <div><label style={labelSt}>Salario Máx (RD$) *</label><input type="number" value={d.salarioMaximo} onChange={e => upd('salarioMaximo',e.target.value)} style={inputSt} required /></div>
          <div><label style={labelSt}>Modalidad</label><select value={d.modalidad} onChange={e => upd('modalidad',e.target.value)} style={inputSt}><option value="Presencial">Presencial</option><option value="Remoto">Remoto</option><option value="Híbrido">Híbrido</option></select></div>
        </div>
        <div><label style={labelSt}>Justificación *</label><textarea value={d.justificacion} onChange={e => upd('justificacion',e.target.value)} rows={3} style={{ ...inputSt, resize:'vertical' }} required /></div>
        <div style={{ display:'flex', gap:'0.75rem', paddingTop:'0.5rem' }}>
          <button type="submit" style={{ flex:1, padding:'0.75rem', backgroundColor:'#1e40af', color:'white', border:'none', borderRadius:'0.625rem', cursor:'pointer', fontWeight:700, fontSize:'0.875rem' }}>Enviar Solicitud</button>
          <button type="button" onClick={onClose} style={{ padding:'0.75rem 1.25rem', backgroundColor:'white', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'0.625rem', cursor:'pointer', fontWeight:600, fontSize:'0.875rem' }}>Cancelar</button>
        </div>
      </form>
    </ModalBase>
  );
};

const ModalVacante = ({ onClose, onGuardar, departamentos }) => {
  const [d, setD] = useState({ titulo:'', departamentoId:'', descripcion:'', requisitos:'', salarioMinimo:'', salarioMaximo:'', modalidad:'Presencial', publicaEnPortal:true });
  const upd = (k,v) => setD(p => ({ ...p, [k]:v }));
  const handleSubmit = (e) => { e.preventDefault(); if (!d.departamentoId) return; onGuardar({ Titulo:d.titulo, DepartamentoID:parseInt(d.departamentoId), Descripcion:d.descripcion, Requisitos:d.requisitos, SalarioMinimo:parseFloat(d.salarioMinimo), SalarioMaximo:parseFloat(d.salarioMaximo), modalidad:d.modalidad, estado:'Activa', PublicaEnPortal: d.publicaEnPortal?1:0 }); };
  return (
    <ModalBase titulo="Crear Nueva Vacante" onClose={onClose} ancho="620px">
      <form onSubmit={handleSubmit} style={{ display:'grid', gap:'0.875rem' }}>
        <div><label style={labelSt}>Título *</label><input value={d.titulo} onChange={e => upd('titulo',e.target.value)} style={inputSt} required /></div>
        <div><label style={labelSt}>Departamento *</label><SearchableSelect value={d.departamentoId} onChange={v => upd('departamentoId', v)} options={departamentos.map(dep => ({ id: dep.id, label: dep.nombre }))} placeholder="Buscar departamento..." emptyLabel="Seleccionar" inputStyle={inputSt} /></div>
        <div><label style={labelSt}>Descripción *</label><textarea value={d.descripcion} onChange={e => upd('descripcion',e.target.value)} rows={3} style={{ ...inputSt, resize:'vertical' }} required /></div>
        <div><label style={labelSt}>Requisitos *</label><textarea value={d.requisitos} onChange={e => upd('requisitos',e.target.value)} rows={3} style={{ ...inputSt, resize:'vertical' }} required /></div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
          <div><label style={labelSt}>Salario Mín (RD$) *</label><input type="number" value={d.salarioMinimo} onChange={e => upd('salarioMinimo',e.target.value)} style={inputSt} required /></div>
          <div><label style={labelSt}>Salario Máx (RD$) *</label><input type="number" value={d.salarioMaximo} onChange={e => upd('salarioMaximo',e.target.value)} style={inputSt} required /></div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
          <div><label style={labelSt}>Modalidad</label><select value={d.modalidad} onChange={e => upd('modalidad',e.target.value)} style={inputSt}><option value="Presencial">Presencial</option><option value="Remoto">Remoto</option><option value="Híbrido">Híbrido</option></select></div>
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', paddingTop:'1.5rem' }}><input type="checkbox" checked={d.publicaEnPortal} onChange={e => upd('publicaEnPortal',e.target.checked)} style={{ width:16, height:16 }} /><label style={{ fontSize:'0.85rem', color:'#374151', fontWeight:600 }}>Publicar en portal externo</label></div>
        </div>
        <div style={{ display:'flex', gap:'0.75rem', paddingTop:'0.5rem' }}>
          <button type="submit" style={{ flex:1, padding:'0.75rem', backgroundColor:'#059669', color:'white', border:'none', borderRadius:'0.625rem', cursor:'pointer', fontWeight:700, fontSize:'0.875rem' }}>Publicar Vacante</button>
          <button type="button" onClick={onClose} style={{ padding:'0.75rem 1.25rem', backgroundColor:'white', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'0.625rem', cursor:'pointer', fontWeight:600, fontSize:'0.875rem' }}>Cancelar</button>
        </div>
      </form>
    </ModalBase>
  );
};

const ModalEditarVacante = ({ vacante, onClose, onGuardar, departamentos }) => {
  const [d, setD] = useState({ titulo:vacante.titulo||'', departamentoId:vacante.departamentoId||'', descripcion:vacante.descripcion||'', requisitos:vacante.requisitos||'', salarioMinimo:vacante.salarioMinimo||'', salarioMaximo:vacante.salarioMaximo||'', modalidad:vacante.modalidad||'Presencial', publicaEnPortal:vacante.PublicaEnPortal===1 });
  const upd = (k,v) => setD(p => ({ ...p, [k]:v }));
  const handleSubmit = (e) => { e.preventDefault(); onGuardar(vacante.id, { Titulo:d.titulo, DepartamentoID:parseInt(d.departamentoId), Descripcion:d.descripcion, Requisitos:d.requisitos, SalarioMinimo:parseFloat(d.salarioMinimo), SalarioMaximo:parseFloat(d.salarioMaximo), Modalidad:d.modalidad, PublicaEnPortal: d.publicaEnPortal?1:0 }); };
  return (
    <ModalBase titulo={`Editar: ${vacante.titulo}`} onClose={onClose} ancho="620px">
      <form onSubmit={handleSubmit} style={{ display:'grid', gap:'0.875rem' }}>
        <div><label style={labelSt}>Título *</label><input value={d.titulo} onChange={e => upd('titulo',e.target.value)} style={inputSt} required /></div>
        <div><label style={labelSt}>Departamento</label><SearchableSelect value={d.departamentoId} onChange={v => upd('departamentoId', v)} options={departamentos.map(dep => ({ id: dep.id, label: dep.nombre }))} placeholder="Buscar departamento..." emptyLabel="Seleccionar" inputStyle={inputSt} /></div>
        <div><label style={labelSt}>Descripción *</label><textarea value={d.descripcion} onChange={e => upd('descripcion',e.target.value)} rows={3} style={{ ...inputSt, resize:'vertical' }} required /></div>
        <div><label style={labelSt}>Requisitos *</label><textarea value={d.requisitos} onChange={e => upd('requisitos',e.target.value)} rows={3} style={{ ...inputSt, resize:'vertical' }} required /></div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
          <div><label style={labelSt}>Salario Mín (RD$) *</label><input type="number" value={d.salarioMinimo} onChange={e => upd('salarioMinimo',e.target.value)} style={inputSt} required /></div>
          <div><label style={labelSt}>Salario Máx (RD$) *</label><input type="number" value={d.salarioMaximo} onChange={e => upd('salarioMaximo',e.target.value)} style={inputSt} required /></div>
        </div>
        <div style={{ display:'flex', gap:'0.75rem', paddingTop:'0.5rem' }}>
          <button type="submit" style={{ flex:1, padding:'0.75rem', backgroundColor:'#1e40af', color:'white', border:'none', borderRadius:'0.625rem', cursor:'pointer', fontWeight:700, fontSize:'0.875rem' }}>Guardar Cambios</button>
          <button type="button" onClick={onClose} style={{ padding:'0.75rem 1.25rem', backgroundColor:'white', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'0.625rem', cursor:'pointer', fontWeight:600, fontSize:'0.875rem' }}>Cancelar</button>
        </div>
      </form>
    </ModalBase>
  );
};

const ModalCerrarVacante = ({ vacante, onClose, onGuardar }) => {
  const [motivo, setMotivo] = useState('');
  return (
    <ModalBase titulo={`Cerrar: ${vacante.titulo}`} onClose={onClose} ancho="480px">
      <div style={{ padding:'0.875rem', backgroundColor:'#fef2f2', borderRadius:'0.625rem', border:'1px solid #fecaca', marginBottom:'1rem', fontSize:'0.8rem', color:'#991b1b' }}>
        <AlertTriangle size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Al cerrar esta vacante no se podrán recibir más postulaciones.
      </div>
      <div style={{ display:'grid', gap:'0.75rem' }}>
        <div><label style={labelSt}>Motivo del Cierre *</label><textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={4} style={{ ...inputSt, resize:'vertical' }} placeholder="Ej: Vacante cubierta con candidato interno" required /></div>
        <div style={{ display:'flex', gap:'0.75rem' }}>
          <button onClick={() => { if (motivo.trim()) onGuardar(vacante.id,motivo); }} disabled={!motivo.trim()} style={{ flex:1, padding:'0.75rem', backgroundColor: motivo.trim()?'#dc2626':'#e2e8f0', color: motivo.trim()?'white':'#94a3b8', border:'none', borderRadius:'0.625rem', cursor: motivo.trim()?'pointer':'not-allowed', fontWeight:700, fontSize:'0.875rem' }}>Cerrar Vacante</button>
          <button onClick={onClose} style={{ padding:'0.75rem 1.25rem', backgroundColor:'white', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'0.625rem', cursor:'pointer', fontWeight:600, fontSize:'0.875rem' }}>Cancelar</button>
        </div>
      </div>
    </ModalBase>
  );
};

// ============================================================
// COMPONENTES AUXILIARES
// ============================================================
const EstadoBadgeLegacy = ({ estado, small }) => {
  const mapa = {
    'Pendiente':                    { bg:'#fef3c7', text:'#92400e', border:'#fcd34d' },
    'Aprobada por Director de Área':{ bg:'#dbeafe', text:'#1e40af', border:'#93c5fd' },
    'Aprobada por Gerente DTHS':    { bg:'#dbeafe', text:'#1e40af', border:'#93c5fd' },
    'Aprobada por Director DTHS':   { bg:'#dcfce7', text:'#166534', border:'#86efac' },
    'Publicada':    { bg:'#ecfeff', text:'#0e7490', border:'#67e8f9' },
    'Rechazada':    { bg:'#fee2e2', text:'#991b1b', border:'#fca5a5' },
    'Activa':       { bg:'#dcfce7', text:'#166534', border:'#86efac' },
    'Cerrada':      { bg:'#f1f5f9', text:'#475569', border:'#cbd5e1' },
    'Pendiente Validación Supervisor':{ bg:'#fef9c3', text:'#854d0e', border:'#fde047' },
    'Rechazada por Supervisor':     { bg:'#fee2e2', text:'#991b1b', border:'#fca5a5' },
    'Enviado':              { bg:'#dbeafe', text:'#1e40af', border:'#93c5fd' },
    'Recibido en DTHS':     { bg:'#ecfeff', text:'#0e7490', border:'#67e8f9' },
    'Visto en DTHS':        { bg:'#f5f3ff', text:'#6d28d9', border:'#c4b5fd' },
    'En Proceso de Revisión':{ bg:'#fffbeb', text:'#92400e', border:'#fcd34d' },
    'Postulación Aprobada': { bg:'#dcfce7', text:'#166534', border:'#86efac' },
    'Aprobación Declinada': { bg:'#fee2e2', text:'#991b1b', border:'#fca5a5' },
    // Estados cortos de la BD — normalizados para el badge
    'Recibida':   { bg:'#ecfeff', text:'#0e7490', border:'#67e8f9' },
    'Nuevo':      { bg:'#dbeafe', text:'#1e40af', border:'#93c5fd' },
    'Nueva':      { bg:'#dbeafe', text:'#1e40af', border:'#93c5fd' },
    'Revisando':  { bg:'#f5f3ff', text:'#6d28d9', border:'#c4b5fd' },
    'Evaluación': { bg:'#fffbeb', text:'#92400e', border:'#fcd34d' },
    'Aprobada':   { bg:'#dcfce7', text:'#166534', border:'#86efac' },
    'Declinada':  { bg:'#fee2e2', text:'#991b1b', border:'#fca5a5' },
  };
  const c = mapa[estado] || { bg:'#f1f5f9', text:'#475569', border:'#cbd5e1' };
  return <span style={{ backgroundColor:c.bg, color:c.text, border:`1px solid ${c.border}`, padding: small?'0.15rem 0.5rem':'0.25rem 0.75rem', borderRadius:'9999px', fontSize: small?'0.7rem':'0.75rem', fontWeight:700, whiteSpace:'nowrap', letterSpacing:'0.01em' }}>{estado}</span>;
};

const EstadoBadgeNew = ({ cfg, label }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.25rem 0.7rem', borderRadius:'9999px', fontSize:'0.7rem', fontWeight:800, background: cfg.light||'#f8fafc', color: cfg.color||'#64748b', border:`1px solid ${cfg.color||'#e2e8f0'}30`, whiteSpace:'nowrap' }}>
    <span style={{ width:6, height:6, borderRadius:'50%', background: cfg.color||'#64748b', display:'inline-block', flexShrink:0 }} />
    {label || cfg.label||cfg.id}
  </span>
);

const TipoBadge = ({ tipo, small }) => {
  const interno = tipo==='Interno';
  return <span style={{ padding: small?'0.15rem 0.5rem':'0.2rem 0.6rem', borderRadius:'9999px', fontSize: small?'0.65rem':'0.7rem', fontWeight:800, background: interno?'#dbeafe':'#fef9c3', color: interno?'#1e40af':'#854d0e' }}>{interno ? '● Interno' : '○ Externo'}</span>;
};

const SeccionFicha = ({ titulo, icono:Icono, children }) => (
  <div style={{ border:'1px solid #f1f5f9', borderRadius:'0.875rem', overflow:'hidden' }}>
    <div style={{ padding:'0.75rem 1rem', background:'#f8fafc', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:'0.5rem' }}>
      <Icono size={14} style={{ color:'#1e40af' }} />
      <span style={{ fontSize:'0.7rem', fontWeight:800, color:'#374151', textTransform:'uppercase', letterSpacing:'0.07em' }}>{titulo}</span>
    </div>
    <div style={{ padding:'1rem' }}>{children}</div>
  </div>
);

const DatoFicha = ({ label, valor }) => (
  <div>
    <div style={{ fontSize:'0.68rem', color:'#94a3b8', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'0.2rem' }}>{label}</div>
    <div style={{ fontSize:'0.875rem', color:'#0f172a', fontWeight:500 }}>{valor || <span style={{ color:'#e2e8f0' }}>—</span>}</div>
  </div>
);

const EmptyState = ({ icono:Icono, titulo, descripcion }) => (
  <div style={{ textAlign:'center', padding:'3rem', backgroundColor:'#fafbfc', borderRadius:'0.875rem', border:'1px dashed #e2e8f0' }}>
    <Icono size={48} style={{ margin:'0 auto 1rem', color:'#cbd5e1' }} />
    <h3 style={{ fontSize:'1rem', fontWeight:700, marginBottom:'0.375rem', color:'#374151' }}>{titulo}</h3>
    <p style={{ color:'#94a3b8', fontSize:'0.875rem', margin:0 }}>{descripcion}</p>
  </div>
);

const Notificacion = ({ type, message, onClose }) => {
  const isError = type==='error';
  return (
    <div style={{ position:'fixed', top:'1rem', right:'1rem', backgroundColor: isError?'#fee2e2':'#dcfce7', border:`1px solid ${isError?'#fca5a5':'#86efac'}`, borderRadius:'0.875rem', padding:'0.875rem 1rem', maxWidth:'380px', zIndex:1100, boxShadow:'0 10px 25px rgba(0,0,0,0.12)', display:'flex', alignItems:'center', gap:'0.75rem', animation:'toastIn 0.3s ease-out' }}>
      {isError ? <XCircle size={18} style={{ color:'#dc2626', flexShrink:0 }} /> : <CheckCircle size={18} style={{ color:'#16a34a', flexShrink:0 }} />}
      <span style={{ flex:1, fontSize:'0.875rem', color: isError?'#991b1b':'#166534', lineHeight:1.4 }}>{message}</span>
      <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color: isError?'#dc2626':'#16a34a', padding:'0.125rem', borderRadius:'0.25rem', display:'flex' }}><X size={14} /></button>
    </div>
  );
};

// ── UTILS ─────────────────────────────────────────────────────────────────────
const parseObservaciones = (obs) => {
  if (!obs) return {};
  const result = {};
  obs.split('\n').forEach(line => {
    const i = line.indexOf(':');
    if (i > -1) {
      const key = line.substring(0,i).trim();
      const val = line.substring(i+1).trim();
      result[key] = val;
    }
  });
  return result;
};

const labelSt = { display:'block', fontSize:'0.8rem', fontWeight:700, marginBottom:'0.375rem', color:'#374151' };
const inputSt  = { width:'100%', padding:'0.625rem 0.75rem', border:'1px solid #e2e8f0', borderRadius:'0.5rem', fontSize:'0.875rem', backgroundColor:'white', outline:'none', boxSizing:'border-box', fontFamily:'inherit', color:'#0f172a', transition:'all 0.2s' };
const selectSt = { padding:'0.6rem 0.875rem', border:'1px solid #e2e8f0', borderRadius:'0.5rem', fontSize:'0.82rem', backgroundColor:'white', cursor:'pointer', transition:'all 0.2s' };

const btnStyle = (color, outline=false) => ({
  padding:'0.55rem 0.875rem',
  backgroundColor: outline ? 'white' : color,
  color: outline ? color : 'white',
  border:`1px solid ${color}`,
  borderRadius:'0.5rem',
  cursor:'pointer',
  fontSize:'0.78rem',
  fontWeight:700,
  display:'flex',
  alignItems:'center',
  gap:'0.35rem',
  transition:'all 0.15s',
  whiteSpace:'nowrap',
});

export default VacantesModule;

